/**
 * Elite Batch 3 Part 1 — Templates 148-172
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after trigger MUST extract named variables
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}

function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 3 PART 1 — TEMPLATES 148-172
// =============================================================================

export const ELITE_BATCH3_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 148. ONBOARDING PLUS CONCIERGE HANDOFF (9 nodes)
  // ===================================================================

  "Onboarding plus Concierge Handoff": {
    nodes: [
      trigger("Signup Webhook: New user registered", "webhook_app", {
        source: "Auth0 / Firebase Auth",
        event: "user.created",
        howItStarts:
          "A new user signs up and triggers the onboarding + concierge pipeline",
        setup:
          "In Auth0 -> Hooks -> Post-User Registration or Firebase Auth -> Cloud Functions -> onCreate, point webhook to your workflow URL. Include user email, name, company domain, and plan.",
        inputFields: [
          { name: "user_email", type: "string", example: "jane@acme.com" },
          { name: "user_name", type: "string", example: "Jane Smith" },
          { name: "company_domain", type: "string", example: "acme.com" },
          { name: "signup_plan", type: "string", example: "enterprise" },
        ],
      }),

      // 2. Clearbit firmographic enrichment
      n("2", "hitApi", "Clearbit firmographic enrichment", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://company-stream.clearbit.com/v2/companies/find?domain={company_domain}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            { name: "category.sector", customName: "sector", type: "string" },
            {
              name: "geo.country",
              customName: "company_country",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI value scoring
      aiNode(
        "3",
        "AI value scoring: concierge eligibility",
        520,
        "You are an account value scoring engine. Score this new signup for concierge eligibility based on company size, revenue, industry fit, and plan tier. Respond JSON: {value_score: number, is_high_value: string, recommended_onboarding: string, concierge_reason: string, priority_level: string}",
        "User: {user_name} ({user_email}), Company: {company_name}, Domain: {company_domain}, Employees: {employee_count}, Revenue: {annual_revenue}, Industry: {industry}, Sector: {sector}, Country: {company_country}, Plan: {signup_plan}",
        [
          { field: "value_score", as: "value_score", type: "number" },
          { field: "is_high_value", as: "is_high_value", type: "string" },
          {
            field: "recommended_onboarding",
            as: "recommended_onboarding",
            type: "string",
          },
          { field: "concierge_reason", as: "concierge_reason", type: "string" },
          { field: "priority_level", as: "priority_level", type: "string" },
        ],
      ),

      // 4. Routing condition: high-value?
      n("4", "condition", "High-value account? (value_score > 75)", 760, {
        conditionData: [
          {
            variable: "is_high_value",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Welcome email (all users)
      emailNode(
        "5",
        "Welcome email with onboarding guide",
        1000,
        "{user_email}",
        "Welcome to {company_name}'s journey with us, {user_name}!",
        "<h2>Welcome aboard, {user_name}!</h2><p>We're thrilled to have {company_name} on board. Here's your personalized getting-started guide:</p><p><strong>Recommended path:</strong> {recommended_onboarding}</p><ul><li>Step 1: Complete your profile setup</li><li>Step 2: Connect your first integration</li><li>Step 3: Create your first workflow</li></ul><p>Your priority level: <strong>{priority_level}</strong></p><p>Need help? Reply to this email anytime.</p>",
      ),

      // 6. Automated onboarding task creation (Asana)
      n("6", "hitApi", "Create onboarding tasks in Asana", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"workspace":"{asana_workspace_id}","projects":["{onboarding_project_id}"],"name":"Onboard: {company_name} ({user_name})","notes":"Value score: {value_score}\\nPriority: {priority_level}\\nOnboarding: {recommended_onboarding}\\nIndustry: {industry}","due_on":"{onboarding_due_date}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "onboarding_task_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Concierge task generation (high-value path)
      n(
        "7",
        "hitApi",
        "Create concierge task in Asana",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"workspace":"{asana_workspace_id}","projects":["{concierge_project_id}"],"name":"CONCIERGE: {company_name} - {user_name} ({priority_level})","notes":"Reason: {concierge_reason}\\nValue score: {value_score}\\nEmployees: {employee_count}\\nRevenue: {annual_revenue}\\nIndustry: {industry}\\nPlan: {signup_plan}","assignee":"{concierge_team_assignee}","due_on":"{concierge_due_date}"}',
              },
            ],
            variables: [
              {
                name: "data.gid",
                customName: "concierge_task_id",
                type: "string",
              },
            ],
          },
        },
        460,
      ),

      // 8. Slack handoff alert
      slackNode(
        "8",
        "Slack: Concierge handoff alert",
        1720,
        "*CONCIERGE HANDOFF*\n\nNew high-value signup requires concierge onboarding:\n*{user_name}* ({user_email})\nCompany: {company_name} | Employees: {employee_count} | Revenue: ${annual_revenue}\nIndustry: {industry} | Plan: {signup_plan}\nValue Score: {value_score} | Priority: {priority_level}\nReason: {concierge_reason}\n\nAsana task: {concierge_task_id}\nOnboarding task: {onboarding_task_id}",
        460,
      ),

      // 9. Exception queue (low-value path)
      slackNode(
        "9",
        "Exception queue: Self-serve onboarding logged",
        1000,
        "Self-serve onboarding initiated:\n{user_name} ({user_email}) at {company_name}\nValue score: {value_score} | Priority: {priority_level}\nOnboarding path: {recommended_onboarding}\nTask: {onboarding_task_id}",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high-value -> welcome + concierge path
      e("4", "5", "output_2"), // low-value also gets welcome email
      e("5", "6"),
      e("4", "7", "output_1"), // high-value -> concierge task
      e("7", "8"),
      e("4", "9", "output_2"), // low-value -> exception queue
    ],
    summary: `**Onboarding plus Concierge Handoff** — AI-scored signup routing with concierge escalation for high-value accounts.\n\n1. New user signup triggers via Auth0/Firebase webhook\n2. **Clearbit enrichment** pulls firmographics: employees, revenue, industry\n3. **AI value scoring** determines concierge eligibility based on company profile and plan\n4. Routing: high-value accounts get concierge treatment, others get self-serve\n5. **Welcome email** sent to all with personalized onboarding guide\n6. **Asana onboarding task** created for tracking\n7. High-value: **Concierge task** created with full context\n8. **Slack alert** notifies concierge team of handoff\n9. Low-value: logged to exception queue for self-serve tracking\n\n**To activate:** Say "connect clearbit", "connect openai", "connect asana", "connect slack", "connect email"`,
  },

  // ===================================================================
  // 149. PRODUCT TUTORIAL NOT COMPLETED (8 nodes)
  // ===================================================================

  "Product Tutorial Not Completed": {
    nodes: [
      trigger("Tutorial stall: User stuck mid-tutorial", "webhook_app", {
        source: "Segment / Amplitude",
        event: "tutorial.stalled",
        howItStarts:
          "A user starts but does not complete a product tutorial within the expected timeframe",
        setup:
          "Configure Segment or Amplitude to fire a webhook when a user starts a tutorial but does not reach the completion event within 2 hours. Include user_id, user_email, user_name, tutorial_id, last_step_completed, and tutorial_name.",
        inputFields: [
          { name: "user_email", type: "string", example: "user@company.com" },
          { name: "user_name", type: "string", example: "Alex Chen" },
          { name: "tutorial_id", type: "string", example: "tut_onboard_01" },
          {
            name: "tutorial_name",
            type: "string",
            example: "Getting Started Guide",
          },
          { name: "last_step_completed", type: "string", example: "3 of 7" },
          { name: "user_phone", type: "string", example: "+15551234567" },
        ],
      }),

      // 2. User context pull
      n("2", "hitApi", "Pull user context from Mixpanel", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://mixpanel.com/api/2.0/engage?project_id={mixpanel_project_id}",
          headers: [
            { key: "Authorization", value: "Basic {mixpanel_service_account}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "filter_by_cohort", value: '{"id":"{user_email}"}' },
            {
              key: "output_properties",
              value:
                '["$name","$email","$last_seen","total_sessions","onboarding_progress","device_type","os"]',
            },
          ],
          variables: [
            {
              name: "results[0].$properties.total_sessions",
              customName: "total_sessions",
              type: "number",
            },
            {
              name: "results[0].$properties.onboarding_progress",
              customName: "onboarding_progress",
              type: "string",
            },
            {
              name: "results[0].$properties.device_type",
              customName: "device_type",
              type: "string",
            },
            {
              name: "results[0].$properties.$last_seen",
              customName: "last_seen",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI friction-aware message
      aiNode(
        "3",
        "AI: Diagnose friction + craft nudge",
        520,
        "You are a product adoption specialist. Analyze why this user stalled in a tutorial and craft a helpful, non-pushy nudge message. Consider their device, session count, and where they stopped. Respond JSON: {friction_hypothesis: string, nudge_subject: string, nudge_body: string, deep_link_step: string, rescue_sms: string, is_completed: string}",
        "User: {user_name} ({user_email}), Tutorial: {tutorial_name} (ID: {tutorial_id}), Last step: {last_step_completed}, Total sessions: {total_sessions}, Device: {device_type}, Last seen: {last_seen}, Onboarding progress: {onboarding_progress}",
        [
          {
            field: "friction_hypothesis",
            as: "friction_hypothesis",
            type: "string",
          },
          { field: "nudge_subject", as: "nudge_subject", type: "string" },
          { field: "nudge_body", as: "nudge_body", type: "string" },
          { field: "deep_link_step", as: "deep_link_step", type: "string" },
          { field: "rescue_sms", as: "rescue_sms", type: "string" },
          { field: "is_completed", as: "is_completed", type: "string" },
        ],
      ),

      // 4. Nudge email with deep link
      emailNode(
        "4",
        "Nudge email: Resume tutorial with deep link",
        760,
        "{user_email}",
        "{nudge_subject}",
        "<p>Hi {user_name},</p><p>{nudge_body}</p><p><a href='https://app.yourproduct.com/tutorial/{tutorial_id}?step={deep_link_step}'>Pick up where you left off</a></p><p>Friction hypothesis (internal): {friction_hypothesis}</p>",
      ),

      // 5. 48hr delay + recheck
      n("5", "delayAction", "Wait 48 hours for tutorial completion", 1000, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
        },
      }),

      // 6. Completion condition
      n("6", "condition", "Tutorial completed after nudge?", 1240, {
        conditionData: [
          {
            variable: "is_completed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. SMS rescue via Twilio (not completed)
      n("7", "sendSMS", "SMS rescue: Tutorial help offer", 1480, {
        sendSmsDetails: {
          to: "{user_phone}",
          message:
            "{rescue_sms} Resume here: https://app.yourproduct.com/tutorial/{tutorial_id}?step={deep_link_step}",
        },
      }),

      // 8. Analytics logging
      n("8", "hitApi", "Log tutorial stall to analytics", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_email}" },
            { key: "event", value: "tutorial_stall_intervention" },
            {
              key: "properties",
              value:
                '{"tutorial_id":"{tutorial_id}","tutorial_name":"{tutorial_name}","last_step":"{last_step_completed}","friction_hypothesis":"{friction_hypothesis}","device":"{device_type}","sessions":"{total_sessions}","nudge_sent":true}',
            },
          ],
          variables: [
            {
              name: "success",
              customName: "analytics_logged",
              type: "boolean",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"), // completed -> just log
      e("6", "7", "output_2"), // not completed -> SMS rescue
      e("7", "8"),
    ],
    summary: `**Product Tutorial Not Completed** — AI-powered friction detection with multi-channel rescue for stalled tutorial users.\n\n1. Tutorial stall detected via Segment/Amplitude webhook\n2. **Mixpanel context pull** retrieves session count, device, onboarding progress\n3. **AI diagnoses friction** and crafts personalized nudge with deep link\n4. **Nudge email** sent with direct link to resume at the stalled step\n5. 48-hour wait for natural completion\n6. Completion check: if done, log success; if not, escalate\n7. **SMS rescue** via Twilio with tutorial resume link\n8. **Analytics logging** tracks intervention and friction hypothesis\n\n**To activate:** Say "connect mixpanel", "connect openai", "connect email", "connect twilio", "connect segment"`,
  },

  // ===================================================================
  // 150. PROFILE COMPLETE NO FIRST PURCHASE (8 nodes)
  // ===================================================================

  "Profile Complete No First Purchase": {
    nodes: [
      trigger("Conversion gap: Profile done, no purchase", "webhook_app", {
        source: "Segment / Internal DB",
        event: "profile.completed_no_purchase",
        howItStarts:
          "A user completes their profile but has not made a first purchase within the expected window",
        setup:
          "Configure a webhook trigger when a user completes profile setup but has zero orders after 72 hours. Include user details, preferences, and profile data.",
        inputFields: [
          {
            name: "user_email",
            type: "string",
            example: "shopper@example.com",
          },
          { name: "user_name", type: "string", example: "Maria Rodriguez" },
          { name: "user_phone", type: "string", example: "+15559876543" },
          {
            name: "preferences",
            type: "string",
            example: "outdoor gear, running shoes, hiking",
          },
          {
            name: "profile_completed_at",
            type: "string",
            example: "2024-01-15T10:30:00Z",
          },
        ],
      }),

      // 2. Preference context assembly
      n("2", "hitApi", "Pull browsing + preference context", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://mixpanel.com/api/2.0/engage?project_id={mixpanel_project_id}",
          headers: [
            { key: "Authorization", value: "Basic {mixpanel_service_account}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "filter_by_cohort", value: '{"id":"{user_email}"}' },
            {
              key: "output_properties",
              value:
                '["$name","viewed_categories","wishlist_items","price_range","browse_count","signup_source"]',
            },
          ],
          variables: [
            {
              name: "results[0].$properties.viewed_categories",
              customName: "viewed_categories",
              type: "string",
            },
            {
              name: "results[0].$properties.wishlist_items",
              customName: "wishlist_items",
              type: "string",
            },
            {
              name: "results[0].$properties.price_range",
              customName: "price_range",
              type: "string",
            },
            {
              name: "results[0].$properties.browse_count",
              customName: "browse_count",
              type: "number",
            },
            {
              name: "results[0].$properties.signup_source",
              customName: "signup_source",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI recommendation generation
      aiNode(
        "3",
        "AI: Generate personalized product recommendations",
        520,
        "You are a product recommendation engine. Based on the user preferences, browsing history, wishlist, and price range, generate 3 personalized product recommendations with compelling reasons to buy. Respond JSON: {rec_1_name: string, rec_1_reason: string, rec_1_url: string, rec_2_name: string, rec_2_reason: string, rec_2_url: string, rec_3_name: string, rec_3_reason: string, rec_3_url: string, email_subject: string, email_intro: string, sms_nudge: string, has_purchased: string}",
        "User: {user_name}, Preferences: {preferences}, Viewed categories: {viewed_categories}, Wishlist: {wishlist_items}, Price range: {price_range}, Browse count: {browse_count}, Signup source: {signup_source}",
        [
          { field: "rec_1_name", as: "rec_1_name", type: "string" },
          { field: "rec_1_reason", as: "rec_1_reason", type: "string" },
          { field: "rec_1_url", as: "rec_1_url", type: "string" },
          { field: "rec_2_name", as: "rec_2_name", type: "string" },
          { field: "rec_2_reason", as: "rec_2_reason", type: "string" },
          { field: "rec_3_name", as: "rec_3_name", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_intro", as: "email_intro", type: "string" },
          { field: "sms_nudge", as: "sms_nudge", type: "string" },
          { field: "has_purchased", as: "has_purchased", type: "string" },
        ],
      ),

      // 4. Catalog validation (runCode)
      n("4", "runCode", "Validate recommendations against live catalog", 760, {
        runCodeData: {
          code: "const recs = [{name: input.rec_1_name, url: input.rec_1_url, reason: input.rec_1_reason}, {name: input.rec_2_name, url: input.rec_2_url || '#', reason: input.rec_2_reason}, {name: input.rec_3_name, url: input.rec_3_url || '#', reason: input.rec_3_reason}]; const valid = recs.filter(r => r.name && r.name.length > 0 && r.url && r.url.startsWith('http')); output.validated_count = valid.length; output.rec_html = valid.map(r => '<li><strong>' + r.name + '</strong> - ' + r.reason + ' <a href=\"' + r.url + '\">Shop now</a></li>').join(''); output.has_valid_recs = valid.length >= 1;",
        },
      }),

      // 5. Recommendation email
      emailNode(
        "5",
        "Personalized recommendation email",
        1000,
        "{user_email}",
        "{email_subject}",
        "<p>Hi {user_name},</p><p>{email_intro}</p><ul>{rec_html}</ul><p>We picked these just for you based on your preferences. Your first purchase ships free!</p>",
      ),

      // 6. Delay + purchase check condition
      n("6", "delayAction", "Wait 72 hours for purchase", 1240, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 72,
          delayUnit: "hours",
        },
      }),

      // 6b. Purchase check condition
      n("6b", "condition", "First purchase made?", 1480, {
        conditionData: [
          {
            variable: "has_purchased",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. SMS escalation
      n("7", "sendSMS", "SMS: First purchase nudge with offer", 1720, {
        sendSmsDetails: {
          to: "{user_phone}",
          message:
            "{sms_nudge} Use code FIRST15 for 15% off your first order: https://shop.example.com/recs/{user_email}",
        },
      }),

      // 8. CRM attribution update
      n("8", "hitApi", "CRM attribution update: conversion gap", 1960, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"conversion_gap_intervention":"true","rec_email_sent":"true","sms_escalation":"true","viewed_categories":"{viewed_categories}","browse_count":"{browse_count}","validated_recs":"{validated_count}"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_update_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "6b"),
      e("6b", "8", "output_1"), // purchased -> CRM update only
      e("6b", "7", "output_2"), // no purchase -> SMS escalation
      e("7", "8"),
    ],
    summary: `**Profile Complete No First Purchase** — AI-powered product recommendations to convert profile-complete users into first-time buyers.\n\n1. Conversion gap detected: profile done, no purchase\n2. **Mixpanel context** pulls browsing history, wishlist, price range\n3. **AI generates 3 personalized recommendations** with compelling reasons\n4. **RunCode validates** recommendations against live catalog\n5. **Recommendation email** with curated picks and free shipping offer\n6. 72-hour wait for natural purchase\n7. No purchase? **SMS escalation** with 15% first-order coupon\n8. **CRM attribution update** logs intervention details\n\n**To activate:** Say "connect mixpanel", "connect openai", "connect email", "connect twilio", "connect hubspot"`,
  },

  // ===================================================================
  // 151. PROGRESSIVE PROFILE ENRICHMENT AFTER OPT-IN (7 nodes + 1 condition = 8)
  // ===================================================================

  "Progressive Profile Enrichment After Opt-In": {
    nodes: [
      trigger("Opt-in received: User grants data permission", "webhook_form", {
        source: "Web Form / Preference Center",
        event: "user.opted_in",
        howItStarts:
          "A user explicitly opts into profile enrichment and personalization",
        setup:
          "Connect your preference center or opt-in form webhook. Include user email, name, and consent scope.",
        inputFields: [
          { name: "user_email", type: "string", example: "opted@example.com" },
          { name: "user_name", type: "string", example: "Jordan Lee" },
          {
            name: "consent_scope",
            type: "string",
            example: "marketing,personalization,analytics",
          },
        ],
      }),

      // 2. Micro-survey welcome email
      emailNode(
        "2",
        "Micro-survey welcome email",
        280,
        "{user_email}",
        "Thanks for opting in, {user_name}! Quick 3-question survey",
        "<p>Hi {user_name},</p><p>Thanks for trusting us with your preferences! We want to personalize your experience.</p><p>Please take 30 seconds to answer 3 quick questions:</p><p><a href='https://app.yourproduct.com/survey/{user_email}?q=role'>What is your role?</a></p><p><a href='https://app.yourproduct.com/survey/{user_email}?q=goals'>What are your top goals?</a></p><p><a href='https://app.yourproduct.com/survey/{user_email}?q=channels'>Which channels do you prefer?</a></p>",
      ),

      // 3. Response ingestion (webhook)
      n("3", "hitApi", "Ingest survey responses", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.outgrow-forms.com/forms/{survey_form_id}/responses?query={user_email}&page_size=1",
          headers: [
            { key: "Authorization", value: "Bearer {outgrow-forms_token}" },
          ],
          variables: [
            {
              name: "items[0].answers[0].text",
              customName: "survey_role",
              type: "string",
            },
            {
              name: "items[0].answers[1].text",
              customName: "survey_goals",
              type: "string",
            },
            {
              name: "items[0].answers[2].text",
              customName: "survey_channels",
              type: "string",
            },
            {
              name: "total_items",
              customName: "survey_responses_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Clearbit enrichment
      n("4", "hitApi", "Clearbit profile enrichment", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={user_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            {
              name: "person.employment.title",
              customName: "job_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "company_name",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "company.geo.country",
              customName: "country",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI profile synthesis + scoring
      aiNode(
        "5",
        "AI: Profile synthesis + segment scoring",
        1000,
        "You are a customer profile synthesizer. Combine survey data with Clearbit enrichment to create a unified profile with segment assignment and engagement score. Respond JSON: {unified_segment: string, engagement_score: number, persona_label: string, recommended_content: string, is_sales_ready: string, profile_summary: string}",
        "User: {user_name} ({user_email}), Survey role: {survey_role}, Survey goals: {survey_goals}, Survey channels: {survey_channels}, Job title: {job_title}, Seniority: {seniority}, Company: {company_name}, Employees: {employee_count}, Industry: {industry}, Country: {country}, Consent: {consent_scope}",
        [
          { field: "unified_segment", as: "unified_segment", type: "string" },
          { field: "engagement_score", as: "engagement_score", type: "number" },
          { field: "persona_label", as: "persona_label", type: "string" },
          {
            field: "recommended_content",
            as: "recommended_content",
            type: "string",
          },
          { field: "is_sales_ready", as: "is_sales_ready", type: "string" },
          { field: "profile_summary", as: "profile_summary", type: "string" },
        ],
      ),

      // 6. CRM update + segmenting
      n("6", "hitApi", "CRM update: Enriched profile + segment", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"persona_label":"{persona_label}","unified_segment":"{unified_segment}","engagement_score":"{engagement_score}","survey_role":"{survey_role}","survey_goals":"{survey_goals}","preferred_channels":"{survey_channels}","job_title":"{job_title}","seniority":"{seniority}","company":"{company_name}","employee_count":"{employee_count}","industry":"{industry}","is_sales_ready":"{is_sales_ready}"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_update_id", type: "string" },
          ],
        },
      }),

      // 7. Threshold condition: sales-ready?
      n("7", "condition", "Sales-ready threshold? (engagement > 70)", 1480, {
        conditionData: [
          {
            variable: "is_sales_ready",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Sales handoff Slack
      slackNode(
        "8",
        "Slack: Sales-ready handoff alert",
        1720,
        "*SALES-READY LEAD via Progressive Enrichment*\n\n*{user_name}* ({user_email})\nPersona: {persona_label} | Segment: {unified_segment}\nScore: {engagement_score} | Seniority: {seniority}\nCompany: {company_name} ({employee_count} employees, {industry})\nGoals: {survey_goals}\nPreferred channels: {survey_channels}\n\nSummary: {profile_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // sales-ready -> Slack handoff
    ],
    summary: `**Progressive Profile Enrichment After Opt-In** — Multi-source profile building with AI synthesis and sales handoff.\n\n1. User explicitly opts into enrichment\n2. **Micro-survey email** collects role, goals, and channel preferences\n3. **Outgrow Forms ingestion** pulls survey responses\n4. **Clearbit enrichment** adds firmographic and employment data\n5. **AI synthesizes** unified profile with segment and engagement scoring\n6. **CRM update** writes enriched profile, segment, and persona\n7. Sales-ready threshold check\n8. If qualified, **Slack handoff** to sales with full profile context\n\n**To activate:** Say "connect outgrow-forms", "connect clearbit", "connect openai", "connect hubspot", "connect slack", "connect email"`,
  },

  // ===================================================================
  // 152. RAG KNOWLEDGE BASE CHATBOT (9 nodes)
  // ===================================================================

  "RAG Knowledge Base Chatbot": {
    nodes: [
      trigger("Chat message: User asks a question", "webhook_app", {
        source: "Intercom / Custom Chat Widget",
        event: "message.created",
        howItStarts:
          "A user sends a question via chat that needs answering from the knowledge base",
        setup:
          "Connect your chat widget or Intercom webhook to fire on new messages. Include user_id, user_email, user_name, message_text, and conversation_id.",
        inputFields: [
          { name: "user_email", type: "string", example: "asker@company.com" },
          { name: "user_name", type: "string", example: "Sam Wilson" },
          {
            name: "message_text",
            type: "string",
            example: "How do I configure SSO?",
          },
          { name: "conversation_id", type: "string", example: "conv_abc123" },
        ],
      }),

      // 2. Query embedding (OpenAI)
      n("2", "hitApi", "Generate query embedding via OpenAI", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/embeddings",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "text-embedding-3-small" },
            { key: "input", value: "{message_text}" },
          ],
          variables: [
            {
              name: "data[0].embedding",
              customName: "query_embedding",
              type: "string",
            },
            {
              name: "usage.total_tokens",
              customName: "embedding_tokens",
              type: "number",
            },
          ],
        },
      }),

      // 3. Vector store retrieval (Pinecone)
      n("3", "hitApi", "Retrieve relevant docs from Pinecone", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index_host}/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{query_embedding}" },
            { key: "topK", value: "5" },
            { key: "includeMetadata", value: "true" },
            { key: "namespace", value: "knowledge_base" },
          ],
          variables: [
            {
              name: "matches[0].metadata.text",
              customName: "doc_chunk_1",
              type: "string",
            },
            {
              name: "matches[1].metadata.text",
              customName: "doc_chunk_2",
              type: "string",
            },
            {
              name: "matches[2].metadata.text",
              customName: "doc_chunk_3",
              type: "string",
            },
            {
              name: "matches[0].score",
              customName: "top_relevance_score",
              type: "number",
            },
            {
              name: "matches[0].metadata.source",
              customName: "source_doc",
              type: "string",
            },
          ],
        },
      }),

      // 4. Relevance condition
      n("4", "condition", "Relevant docs found? (score > 0.75)", 760, {
        conditionData: [
          {
            variable: "top_relevance_score",
            value: "0.75",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Grounded answer generation
      aiNode(
        "5",
        "AI: Generate grounded answer from docs",
        1000,
        "You are a helpful knowledge base assistant. Answer the user question ONLY using the provided context documents. If the context does not contain enough information, say so clearly. Always cite the source document. Never make up information. Respond JSON: {answer: string, confidence: string, source_citation: string, needs_human: string, answer_quality: string}",
        "Question: {message_text}\\n\\nContext documents:\\n1. {doc_chunk_1}\\n2. {doc_chunk_2}\\n3. {doc_chunk_3}\\nSource: {source_doc}",
        [
          { field: "answer", as: "generated_answer", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "string" },
          { field: "source_citation", as: "source_citation", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
          { field: "answer_quality", as: "answer_quality", type: "string" },
        ],
      ),

      // 6. Chat delivery
      n("6", "sendMessage", "Deliver answer to chat conversation", 1240, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message: "{generated_answer}\n\nSource: {source_citation}",
        },
      }),

      // 7. Quality logging
      n("7", "hitApi", "Log query + answer quality to analytics", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_email}" },
            { key: "event", value: "rag_query_answered" },
            {
              key: "properties",
              value:
                '{"question":"{message_text}","relevance_score":"{top_relevance_score}","confidence":"{answer_confidence}","quality":"{answer_quality}","needs_human":"{needs_human}","source":"{source_doc}","embedding_tokens":"{embedding_tokens}"}',
            },
          ],
          variables: [
            { name: "success", customName: "quality_logged", type: "boolean" },
          ],
        },
      }),

      // 8. Human escalation (Zendesk ticket) — low relevance / needs human
      n(
        "8",
        "hitApi",
        "Create Zendesk ticket for human escalation",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"subject":"RAG escalation: {message_text}","comment":{"body":"User {user_name} ({user_email}) asked:\\n{message_text}\\n\\nAI could not find a confident answer (relevance: {top_relevance_score}).\\nConversation: {conversation_id}"},"requester":{"email":"{user_email}","name":"{user_name}"},"priority":"normal","tags":["rag_escalation","knowledge_gap"]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "zendesk_ticket_id",
                type: "string",
              },
            ],
          },
        },
        460,
      ),

      // 9. Support Slack alert
      slackNode(
        "9",
        "Slack: RAG escalation alert to support",
        1240,
        "*RAG ESCALATION — Knowledge gap detected*\n\nUser: {user_name} ({user_email})\nQuestion: {message_text}\nRelevance score: {top_relevance_score}\nZendesk ticket: {zendesk_ticket_id}\nConversation: {conversation_id}\n\nThis question could not be confidently answered from the knowledge base.",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // relevant -> generate answer
      e("5", "6"),
      e("6", "7"),
      e("4", "8", "output_2"), // not relevant -> human escalation
      e("8", "9"),
    ],
    summary: `**RAG Knowledge Base Chatbot** — Retrieval-augmented generation pipeline with vector search, grounded answers, and human escalation.\n\n1. User question arrives via chat webhook\n2. **OpenAI embedding** converts query to vector\n3. **Pinecone vector search** retrieves top 5 matching knowledge base chunks\n4. Relevance check: score > 0.75 proceeds to AI answer\n5. **AI generates grounded answer** citing source documents, never hallucinating\n6. **Answer delivered** to chat conversation\n7. **Quality logging** tracks relevance, confidence, and token usage\n8. Low relevance: **Zendesk ticket** created for human review\n9. **Slack alert** notifies support of knowledge gap\n\n**To activate:** Say "connect openai", "connect pinecone", "connect zendesk", "connect slack", "connect segment"`,
  },

  // ===================================================================
  // 153. REFILL RECOMMENDATION BY USAGE SCORE (8 nodes)
  // ===================================================================

  "Refill Recommendation by Usage Score": {
    nodes: [
      trigger("Daily depletion scan: Products nearing refill", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Daily scheduled scan identifies customers whose products are likely running low based on usage patterns",
        setup:
          "Activate this workflow. It runs daily and checks order history + usage patterns to predict refill timing.",
        inputFields: [
          {
            name: "customer_email",
            type: "string",
            example: "customer@example.com",
          },
          { name: "customer_name", type: "string", example: "Pat Johnson" },
          { name: "customer_phone", type: "string", example: "+15551112222" },
          { name: "customer_id", type: "string", example: "cust_12345" },
        ],
      }),

      // 2. Order history pull
      n("2", "hitApi", "Pull order history from Shopify", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/customers/{customer_id}/orders.json?status=any&limit=10",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          variables: [
            {
              name: "orders[0].line_items[0].title",
              customName: "last_product_name",
              type: "string",
            },
            {
              name: "orders[0].line_items[0].sku",
              customName: "last_product_sku",
              type: "string",
            },
            {
              name: "orders[0].created_at",
              customName: "last_order_date",
              type: "string",
            },
            {
              name: "orders[0].line_items[0].quantity",
              customName: "last_quantity",
              type: "number",
            },
            {
              name: "orders.length",
              customName: "total_orders",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI usage-score calculation
      aiNode(
        "3",
        "AI: Calculate usage score + refill timing",
        520,
        "You are a product usage analyst. Based on order history, calculate a depletion score (0-100) predicting how soon the customer needs a refill. Factor in order frequency, quantity, and typical product lifespan. Recommend add-on products. Respond JSON: {depletion_score: number, days_until_empty: number, refill_product: string, refill_sku: string, addon_product: string, addon_reason: string, refill_message: string, sms_reminder: string, needs_refill: string}",
        "Customer: {customer_name}, Last product: {last_product_name} (SKU: {last_product_sku}), Last order: {last_order_date}, Quantity: {last_quantity}, Total orders: {total_orders}",
        [
          { field: "depletion_score", as: "depletion_score", type: "number" },
          { field: "days_until_empty", as: "days_until_empty", type: "number" },
          { field: "refill_product", as: "refill_product", type: "string" },
          { field: "refill_sku", as: "refill_sku", type: "string" },
          { field: "addon_product", as: "addon_product", type: "string" },
          { field: "addon_reason", as: "addon_reason", type: "string" },
          { field: "refill_message", as: "refill_message", type: "string" },
          { field: "sms_reminder", as: "sms_reminder", type: "string" },
          { field: "needs_refill", as: "needs_refill", type: "string" },
        ],
      ),

      // 4. Refill + add-on selection — product validation (runCode)
      n("4", "runCode", "Validate refill product + build cart link", 760, {
        runCodeData: {
          code: "const sku = input.refill_sku; const addon = input.addon_product; const valid = sku && sku.length > 0; output.cart_url = valid ? 'https://shop.example.com/cart/add?sku=' + sku + '&addon=' + encodeURIComponent(addon) : 'https://shop.example.com/reorder'; output.is_valid_sku = valid; output.refill_summary = input.refill_product + (addon ? ' + ' + addon : ''); output.cart_ready = true;",
        },
      }),

      // 5. Refill reminder email
      emailNode(
        "5",
        "Refill reminder email with recommendations",
        1000,
        "{customer_email}",
        "Time to restock, {customer_name}! Your {refill_product} is running low",
        "<p>Hi {customer_name},</p><p>{refill_message}</p><p><strong>Your refill:</strong> {refill_product}</p><p><strong>Recommended add-on:</strong> {addon_product} — {addon_reason}</p><p><a href='{cart_url}'>Reorder now with one click</a></p><p>Estimated days until empty: {days_until_empty}</p>",
      ),

      // 6. Delay + reorder condition
      n("6", "delayAction", "Wait 5 days for reorder", 1240, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
        },
      }),

      // 6b. Reorder check
      n("6b", "condition", "Customer reordered?", 1480, {
        conditionData: [
          {
            variable: "needs_refill",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. CRM update
      n("7", "hitApi", "CRM update: Refill intervention logged", 1720, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"last_refill_reminder":"{current_date}","depletion_score":"{depletion_score}","refill_product":"{refill_product}","days_until_empty":"{days_until_empty}","refill_intervention":"sent"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_update_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "6b"),
      e("6b", "7", "output_1"), // reordered -> CRM update
      e("6b", "7", "output_2"), // not reordered -> still log
    ],
    summary: `**Refill Recommendation by Usage Score** — AI-powered depletion scoring with personalized refill reminders and add-on recommendations.\n\n1. Daily scheduled scan identifies customers with products nearing depletion\n2. **Shopify order history** pull retrieves last products, quantities, and dates\n3. **AI calculates depletion score** and predicts days until empty\n4. **RunCode validates** refill SKU and builds one-click reorder link\n5. **Refill email** with personalized product + add-on recommendations\n6. 5-day wait for natural reorder\n7. **CRM update** logs depletion score and intervention details\n\n**To activate:** Say "connect shopify", "connect openai", "connect email", "connect hubspot"`,
  },

  // ===================================================================
  // 154. REGISTRATION NO PROFILE COMPLETION (7 nodes)
  // ===================================================================

  "Registration No Profile Completion": {
    nodes: [
      trigger("Registration + inactivity: Profile incomplete", "webhook_app", {
        source: "Auth0 / Internal System",
        event: "user.profile_incomplete",
        howItStarts:
          "A user registered but did not complete their profile within 24 hours",
        setup:
          "Configure a delayed webhook that fires 24 hours after registration if profile_completed remains false. Include user email, name, phone, and registration date.",
        inputFields: [
          { name: "user_email", type: "string", example: "new@user.com" },
          { name: "user_name", type: "string", example: "Taylor Kim" },
          { name: "user_phone", type: "string", example: "+15557778888" },
          {
            name: "registration_date",
            type: "string",
            example: "2024-01-15T08:00:00Z",
          },
          { name: "profile_fields_filled", type: "string", example: "2 of 6" },
        ],
      }),

      // 2. Partial profile read
      n("2", "hitApi", "Read partial profile data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourproduct.com/v1/users/{user_email}/profile",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          variables: [
            {
              name: "profile.avatar_set",
              customName: "has_avatar",
              type: "boolean",
            },
            { name: "profile.bio", customName: "has_bio", type: "boolean" },
            {
              name: "profile.interests",
              customName: "interests_selected",
              type: "boolean",
            },
            {
              name: "profile.company_name",
              customName: "company_entered",
              type: "boolean",
            },
            {
              name: "profile.completion_pct",
              customName: "completion_pct",
              type: "number",
            },
            {
              name: "profile.last_active",
              customName: "last_active",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI motivational nudge
      aiNode(
        "3",
        "AI: Craft motivational completion nudge",
        520,
        "You are a user engagement specialist. Based on what profile fields are missing, craft a short motivational email nudge that emphasizes the specific benefits of completing each missing field. Be encouraging, not pushy. Respond JSON: {nudge_subject: string, nudge_body: string, missing_fields_summary: string, completion_benefit: string, rescue_sms: string, is_completed: string}",
        "User: {user_name} ({user_email}), Fields filled: {profile_fields_filled}, Avatar: {has_avatar}, Bio: {has_bio}, Interests: {interests_selected}, Company: {company_entered}, Completion: {completion_pct}%, Last active: {last_active}, Registered: {registration_date}",
        [
          { field: "nudge_subject", as: "nudge_subject", type: "string" },
          { field: "nudge_body", as: "nudge_body", type: "string" },
          {
            field: "missing_fields_summary",
            as: "missing_fields_summary",
            type: "string",
          },
          {
            field: "completion_benefit",
            as: "completion_benefit",
            type: "string",
          },
          { field: "rescue_sms", as: "rescue_sms", type: "string" },
          { field: "is_completed", as: "is_completed", type: "string" },
        ],
      ),

      // 4. Completion email with deep link
      emailNode(
        "4",
        "Profile completion email with deep link",
        760,
        "{user_email}",
        "{nudge_subject}",
        "<p>Hi {user_name},</p><p>{nudge_body}</p><p><strong>What you're missing:</strong> {missing_fields_summary}</p><p><strong>Why it matters:</strong> {completion_benefit}</p><p><a href='https://app.yourproduct.com/profile/complete?user={user_email}'>Complete your profile now</a></p>",
      ),

      // 5. Delay + recheck condition
      n("5", "delayAction", "Wait 48 hours for profile completion", 1000, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
        },
      }),

      // 5b. Completion check
      n("5b", "condition", "Profile completed after email?", 1240, {
        conditionData: [
          {
            variable: "is_completed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. SMS rescue via Twilio
      n("6", "sendSMS", "SMS rescue: Profile completion reminder", 1480, {
        sendSmsDetails: {
          to: "{user_phone}",
          message:
            "{rescue_sms} Complete your profile: https://app.yourproduct.com/profile/complete",
        },
      }),

      // 7. Growth queue logging
      n("7", "hitApi", "Log to growth queue: Profile abandonment", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_email}" },
            { key: "event", value: "profile_completion_intervention" },
            {
              key: "properties",
              value:
                '{"completion_pct":"{completion_pct}","missing_fields":"{missing_fields_summary}","email_sent":true,"sms_sent":true,"registration_date":"{registration_date}"}',
            },
          ],
          variables: [
            { name: "success", customName: "growth_logged", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "5b"),
      e("5b", "7", "output_1"), // completed -> just log
      e("5b", "6", "output_2"), // not completed -> SMS rescue
      e("6", "7"),
    ],
    summary: `**Registration No Profile Completion** — AI-powered motivational nudges for incomplete profiles with multi-channel rescue.\n\n1. Incomplete profile detected 24 hours after registration\n2. **Partial profile read** identifies which fields are missing\n3. **AI crafts motivational nudge** highlighting specific benefits of each missing field\n4. **Completion email** with deep link directly to profile editor\n5. 48-hour wait for natural completion\n6. Still incomplete? **SMS rescue** via Twilio\n7. **Growth queue logging** tracks intervention and completion rates\n\n**To activate:** Say "connect openai", "connect email", "connect twilio", "connect segment"`,
  },

  // ===================================================================
  // 155. SMS QUIET-HOURS SAFE-SEND WRAPPER (7 nodes)
  // ===================================================================

  "SMS Quiet-Hours Safe-Send Wrapper": {
    nodes: [
      trigger("SMS request: Outbound message queued", "webhook_app", {
        source: "Internal Workflow / API",
        event: "sms.send_requested",
        howItStarts:
          "An SMS send request arrives and must be checked against quiet-hours policies before delivery",
        setup:
          "Point any internal SMS-sending workflow to this wrapper endpoint. Include recipient phone, message, timezone (if known), and priority level.",
        inputFields: [
          { name: "recipient_phone", type: "string", example: "+15551234567" },
          { name: "recipient_name", type: "string", example: "Chris Adams" },
          {
            name: "message_body",
            type: "string",
            example: "Your order is ready for pickup!",
          },
          {
            name: "recipient_timezone",
            type: "string",
            example: "America/New_York",
          },
          { name: "priority", type: "string", example: "normal" },
        ],
      }),

      // 2. Timezone resolution (runCode)
      n("2", "runCode", "Resolve timezone + determine local time", 280, {
        runCodeData: {
          code: "const tz = input.recipient_timezone || 'America/New_York'; const now = new Date(); const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false }); const localHour = parseInt(formatter.format(now)); output.local_hour = localHour; output.timezone_resolved = tz; output.is_quiet_hours = (localHour >= 21 || localHour < 8) ? 'yes' : 'no'; output.next_send_hour = localHour < 8 ? (8 - localHour) : (24 - localHour + 8); output.policy_check = 'passed';",
        },
      }),

      // 3. Quiet-hours + policy condition
      n("3", "condition", "Within sending hours? (8AM-9PM local)", 520, {
        conditionData: [
          {
            variable: "is_quiet_hours",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Immediate send path (Twilio)
      n("4", "sendSMS", "Immediate SMS send via Twilio", 760, {
        sendSmsDetails: {
          to: "{recipient_phone}",
          message: "{message_body}",
        },
      }),

      // 5. Deferred-send queue (delay)
      n(
        "5",
        "delayAction",
        "Defer until next sending window",
        760,
        {
          delayActionData: {
            delayType: "fixed",
            delayValue: 1,
            delayUnit: "hours",
          },
        },
        460,
      ),

      // 5b. Send after delay
      n(
        "5b",
        "sendSMS",
        "Deferred SMS send via Twilio",
        1000,
        {
          sendSmsDetails: {
            to: "{recipient_phone}",
            message: "{message_body}",
          },
        },
        460,
      ),

      // 6. Delivery logging
      n("6", "hitApi", "Log SMS delivery event", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{recipient_phone}" },
            { key: "event", value: "sms_delivered" },
            {
              key: "properties",
              value:
                '{"recipient":"{recipient_name}","timezone":"{timezone_resolved}","local_hour":"{local_hour}","was_deferred":"{is_quiet_hours}","priority":"{priority}","policy_check":"{policy_check}"}',
            },
          ],
          variables: [
            { name: "success", customName: "delivery_logged", type: "boolean" },
          ],
        },
      }),

      // 7. Ops failure alert (Slack)
      slackNode(
        "7",
        "Slack: SMS delivery failure alert",
        1480,
        "*SMS DELIVERY ISSUE*\n\nRecipient: {recipient_name} ({recipient_phone})\nTimezone: {timezone_resolved} | Local hour: {local_hour}\nQuiet hours: {is_quiet_hours} | Priority: {priority}\nMessage: {message_body}\n\nCheck Twilio dashboard for delivery status.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // within hours -> immediate send
      e("3", "5", "output_2"), // quiet hours -> defer
      e("5", "5b"),
      e("4", "6"),
      e("5b", "6"),
      e("6", "7"),
    ],
    summary: `**SMS Quiet-Hours Safe-Send Wrapper** — Policy-compliant SMS delivery with timezone resolution and quiet-hours deferral.\n\n1. SMS send request arrives via internal webhook\n2. **RunCode resolves timezone** and calculates local hour\n3. Quiet-hours check: 8AM-9PM local time is the sending window\n4. Within hours: **Immediate SMS send** via Twilio\n5. Quiet hours: **Deferred to next window** via delay node\n6. **Delivery logging** tracks timezone, deferral status, and policy compliance\n7. **Slack alert** for any delivery issues\n\n**To activate:** Say "connect twilio", "connect segment", "connect slack"`,
  },

  // ===================================================================
  // 156. SMS RESCUE FOR HIGH-INTENT NEW USER (8 nodes)
  // ===================================================================

  "SMS Rescue for High-Intent New User": {
    nodes: [
      trigger(
        "High-intent behavior: New user showing buying signals",
        "webhook_app",
        {
          source: "Segment / Amplitude",
          event: "user.high_intent_detected",
          howItStarts:
            "A new user exhibits high-intent behavior (pricing page visit, feature comparison, demo request) but hasn't converted",
          setup:
            "Configure Segment to fire when a new user (< 7 days) visits pricing page 3+ times, views comparison page, or starts checkout without completing. Include user details and behavioral signals.",
          inputFields: [
            {
              name: "user_email",
              type: "string",
              example: "prospect@company.com",
            },
            { name: "user_name", type: "string", example: "Morgan Blake" },
            { name: "user_phone", type: "string", example: "+15553334444" },
            {
              name: "intent_signals",
              type: "string",
              example: "pricing_page:5,comparison:2,demo_start:1",
            },
            {
              name: "user_timezone",
              type: "string",
              example: "America/Chicago",
            },
          ],
        },
      ),

      // 2. Behavioral context pull
      n("2", "hitApi", "Pull behavioral context from Mixpanel", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://mixpanel.com/api/2.0/engage?project_id={mixpanel_project_id}",
          headers: [
            { key: "Authorization", value: "Basic {mixpanel_service_account}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "filter_by_cohort", value: '{"id":"{user_email}"}' },
            {
              key: "output_properties",
              value:
                '["$name","total_sessions","pages_viewed","pricing_views","feature_interactions","time_on_site","referral_source"]',
            },
          ],
          variables: [
            {
              name: "results[0].$properties.total_sessions",
              customName: "total_sessions",
              type: "number",
            },
            {
              name: "results[0].$properties.pricing_views",
              customName: "pricing_views",
              type: "number",
            },
            {
              name: "results[0].$properties.feature_interactions",
              customName: "feature_interactions",
              type: "string",
            },
            {
              name: "results[0].$properties.time_on_site",
              customName: "time_on_site",
              type: "number",
            },
            {
              name: "results[0].$properties.referral_source",
              customName: "referral_source",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI intent scoring + objection hypothesis
      aiNode(
        "3",
        "AI: Intent scoring + objection analysis",
        520,
        "You are a conversion optimization specialist. Score this user intent (0-100) and hypothesize their likely objections. Craft a personalized rescue SMS that addresses the most likely objection. Respond JSON: {intent_score: number, primary_objection: string, secondary_objection: string, rescue_copy: string, urgency_hook: string, offer_type: string, is_quiet_hours: string}",
        "User: {user_name} ({user_email}), Intent signals: {intent_signals}, Sessions: {total_sessions}, Pricing views: {pricing_views}, Features explored: {feature_interactions}, Time on site: {time_on_site}min, Referral: {referral_source}, Timezone: {user_timezone}",
        [
          { field: "intent_score", as: "intent_score", type: "number" },
          {
            field: "primary_objection",
            as: "primary_objection",
            type: "string",
          },
          {
            field: "secondary_objection",
            as: "secondary_objection",
            type: "string",
          },
          { field: "rescue_copy", as: "rescue_copy", type: "string" },
          { field: "urgency_hook", as: "urgency_hook", type: "string" },
          { field: "offer_type", as: "offer_type", type: "string" },
          { field: "is_quiet_hours", as: "is_quiet_hours", type: "string" },
        ],
      ),

      // 4. Rescue copy generation — already done by AI, pass through
      n("4", "runCode", "Format rescue SMS with tracking link", 760, {
        runCodeData: {
          code: "output.final_sms = input.rescue_copy + ' ' + input.urgency_hook + ' Book a quick demo: https://app.yourproduct.com/demo?ref=' + encodeURIComponent(input.user_email); output.sms_ready = true;",
        },
      }),

      // 5. Quiet-hours routing (condition)
      n("5", "condition", "Within sending hours?", 1000, {
        conditionData: [
          {
            variable: "is_quiet_hours",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Tracked SMS send (Twilio)
      n("6", "sendSMS", "Send rescue SMS via Twilio", 1240, {
        sendSmsDetails: {
          to: "{user_phone}",
          message: "{final_sms}",
        },
      }),

      // 7. Delay + outcome recheck condition
      n("7", "delayAction", "Wait 24 hours for conversion", 1480, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 24,
          delayUnit: "hours",
        },
      }),

      // 8. CRM task creation
      n("8", "hitApi", "Create CRM follow-up task", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_task_subject":"Follow up: High-intent user {user_name}","hs_task_body":"Intent score: {intent_score}\\nPrimary objection: {primary_objection}\\nSecondary objection: {secondary_objection}\\nOffer type: {offer_type}\\nSMS sent: {final_sms}\\nPricing views: {pricing_views}","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH"}',
            },
          ],
          variables: [{ name: "id", customName: "task_id", type: "string" }],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // within hours -> send now
      e("5", "7", "output_2"), // quiet hours -> skip to delay
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**SMS Rescue for High-Intent New User** — AI-powered objection analysis and personalized SMS rescue for users showing buying signals.\n\n1. High-intent behavior detected (pricing views, feature comparison, demo start)\n2. **Mixpanel context** pulls session depth, pricing views, feature interactions\n3. **AI scores intent** and hypothesizes objections with tailored rescue copy\n4. **RunCode formats** SMS with tracking link\n5. Quiet-hours check ensures compliance\n6. **Rescue SMS** via Twilio with objection-addressing copy\n7. 24-hour conversion window\n8. **CRM task** created for sales follow-up with full intent context\n\n**To activate:** Say "connect mixpanel", "connect openai", "connect twilio", "connect hubspot"`,
  },

  // ===================================================================
  // 157. SAAS INACTIVE POWER-USER DOWNGRADE RISK (9 nodes)
  // ===================================================================

  "SaaS Inactive Power-User Downgrade Risk": {
    nodes: [
      trigger("Usage decline: Power-user activity drop detected", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Weekly scan identifies power-users whose usage has declined significantly, signaling downgrade or churn risk",
        setup:
          "Activate this workflow. It runs weekly to detect power-users whose activity dropped > 40% week-over-week.",
        inputFields: [
          {
            name: "user_email",
            type: "string",
            example: "poweruser@client.com",
          },
          { name: "user_name", type: "string", example: "Jamie Torres" },
          { name: "account_id", type: "string", example: "acct_67890" },
          { name: "usage_drop_pct", type: "string", example: "55" },
          { name: "plan_tier", type: "string", example: "enterprise" },
        ],
      }),

      // 2. Account state pull (Stripe + product)
      n("2", "hitApi", "Pull subscription state from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/subscriptions?customer={stripe_customer_id}&limit=1",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          variables: [
            {
              name: "data[0].id",
              customName: "subscription_id",
              type: "string",
            },
            {
              name: "data[0].plan.amount",
              customName: "monthly_amount",
              type: "number",
            },
            {
              name: "data[0].plan.nickname",
              customName: "plan_name",
              type: "string",
            },
            {
              name: "data[0].current_period_end",
              customName: "renewal_date",
              type: "number",
            },
            {
              name: "data[0].cancel_at_period_end",
              customName: "cancel_pending",
              type: "boolean",
            },
            {
              name: "data[0].status",
              customName: "subscription_status",
              type: "string",
            },
          ],
        },
      }),

      // 3. CRM + support enrichment
      n("3", "hitApi", "Pull CRM + support ticket history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?properties=last_nps_score,support_tickets_open,csm_owner,account_health,last_contacted",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          variables: [
            {
              name: "properties.last_nps_score",
              customName: "nps_score",
              type: "string",
            },
            {
              name: "properties.support_tickets_open",
              customName: "open_tickets",
              type: "string",
            },
            {
              name: "properties.csm_owner",
              customName: "csm_owner",
              type: "string",
            },
            {
              name: "properties.account_health",
              customName: "account_health",
              type: "string",
            },
            {
              name: "properties.last_contacted",
              customName: "last_contacted",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI risk scoring
      aiNode(
        "4",
        "AI: Churn risk scoring + intervention plan",
        760,
        "You are a customer success analyst. Score the churn risk (0-100) for this declining power-user. Factor in usage drop, subscription value, NPS, open tickets, and account health. Recommend the best intervention. Respond JSON: {risk_score: number, risk_level: string, churn_probability: string, primary_driver: string, intervention_plan: string, email_subject: string, email_body: string, retention_offer: string, is_critical: string}",
        "User: {user_name} ({user_email}), Usage drop: {usage_drop_pct}%, Plan: {plan_name} (${monthly_amount}/mo), Renewal: {renewal_date}, Cancel pending: {cancel_pending}, Status: {subscription_status}, NPS: {nps_score}, Open tickets: {open_tickets}, CSM: {csm_owner}, Account health: {account_health}, Last contacted: {last_contacted}",
        [
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "churn_probability",
            as: "churn_probability",
            type: "string",
          },
          { field: "primary_driver", as: "primary_driver", type: "string" },
          {
            field: "intervention_plan",
            as: "intervention_plan",
            type: "string",
          },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "retention_offer", as: "retention_offer", type: "string" },
          { field: "is_critical", as: "is_critical", type: "string" },
        ],
      ),

      // 5. Severity condition
      n("5", "condition", "Critical risk? (risk_score > 70)", 1000, {
        conditionData: [
          {
            variable: "is_critical",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Re-engagement email
      emailNode(
        "6",
        "Re-engagement email: Address declining usage",
        1240,
        "{user_email}",
        "{email_subject}",
        "<p>Hi {user_name},</p><p>{email_body}</p><p>We noticed your usage has changed and want to make sure you're getting maximum value from your {plan_name} plan.</p><p>Intervention: {intervention_plan}</p>",
      ),

      // 7. CSM Slack escalation (critical path)
      slackNode(
        "7",
        "Slack: CSM escalation for critical churn risk",
        1480,
        "*CRITICAL CHURN RISK — Power-User Decline*\n\nUser: {user_name} ({user_email})\nRisk score: {risk_score}/100 | Level: {risk_level}\nChurn probability: {churn_probability}\nPrimary driver: {primary_driver}\n\nPlan: {plan_name} (${monthly_amount}/mo) | Renewal: {renewal_date}\nCancel pending: {cancel_pending} | NPS: {nps_score}\nOpen tickets: {open_tickets} | CSM: {csm_owner}\n\nIntervention: {intervention_plan}\nRetention offer: {retention_offer}",
        460,
      ),

      // 8. Retention offer (approval gate)
      n(
        "8",
        "approvalGate",
        "Approval: Retention offer for at-risk account",
        1720,
        {
          approvalGateData: {
            message:
              "Retention offer for {user_name} at risk of churn (score: {risk_score}/100).\n\nCurrent plan: {plan_name} (${monthly_amount}/mo)\nProposed offer: {retention_offer}\nPrimary driver: {primary_driver}\n\nApprove to extend the retention offer, reject to skip.",
          },
        },
        460,
      ),

      // 9. Risk event logging
      n("9", "hitApi", "Log risk event to CRM + analytics", 1960, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"churn_risk_score":"{risk_score}","risk_level":"{risk_level}","usage_drop_pct":"{usage_drop_pct}","primary_churn_driver":"{primary_driver}","intervention_plan":"{intervention_plan}","retention_offer_sent":"{retention_offer}","last_risk_assessment":"{current_date}"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_risk_update_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // critical -> email + escalation
      e("5", "6", "output_2"), // non-critical -> email only
      e("5", "7", "output_1"), // critical -> CSM Slack
      e("7", "8"),
      e("8", "9"),
      e("6", "9"),
    ],
    summary: `**SaaS Inactive Power-User Downgrade Risk** — Multi-source churn detection with AI risk scoring and tiered intervention.\n\n1. Weekly scan detects power-users with significant usage decline\n2. **Stripe pull** retrieves subscription value, plan, and renewal info\n3. **CRM enrichment** adds NPS, open tickets, CSM owner, and health score\n4. **AI scores churn risk** with intervention plan and retention offer\n5. Severity routing: critical vs. non-critical\n6. **Re-engagement email** sent to all at-risk users\n7. Critical: **CSM Slack escalation** with full context\n8. Critical: **Approval gate** for retention offer\n9. **CRM logging** records risk score, drivers, and intervention\n\n**To activate:** Say "connect stripe", "connect hubspot", "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 158. SMART EMAIL DRAFTING AGENT (8 nodes)
  // ===================================================================

  "Smart Email Drafting Agent": {
    nodes: [
      trigger("Draft request: User requests email draft", "webhook_app", {
        source: "Slack / Internal Tool",
        event: "email.draft_requested",
        howItStarts:
          "A user requests an AI-drafted email by providing recipient, context, and desired tone",
        setup:
          "Connect via Slack slash command or internal tool webhook. Include recipient_email, sender context, desired tone, and key points to cover.",
        inputFields: [
          {
            name: "recipient_email",
            type: "string",
            example: "client@partner.com",
          },
          { name: "sender_email", type: "string", example: "rep@company.com" },
          { name: "sender_name", type: "string", example: "Alex Rivera" },
          {
            name: "key_points",
            type: "string",
            example:
              "Follow up on Q4 proposal, address pricing concern, propose next meeting",
          },
          {
            name: "desired_tone",
            type: "string",
            example: "professional but warm",
          },
        ],
      }),

      // 2. Recipient CRM enrichment
      n("2", "hitApi", "Enrich recipient from CRM", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{recipient_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["firstname","lastname","company","jobtitle","last_meeting_date","deal_stage","notes_last_contacted"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.firstname",
              customName: "recipient_first",
              type: "string",
            },
            {
              name: "results[0].properties.lastname",
              customName: "recipient_last",
              type: "string",
            },
            {
              name: "results[0].properties.company",
              customName: "recipient_company",
              type: "string",
            },
            {
              name: "results[0].properties.jobtitle",
              customName: "recipient_title",
              type: "string",
            },
            {
              name: "results[0].properties.deal_stage",
              customName: "deal_stage",
              type: "string",
            },
            {
              name: "results[0].properties.notes_last_contacted",
              customName: "last_notes",
              type: "string",
            },
          ],
        },
      }),

      // 3. Thread history retrieval
      n("3", "hitApi", "Retrieve recent email thread from Gmail", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://gmail.googleapis.com/gmail/v1/users/me/messages?q=to:{recipient_email}+OR+from:{recipient_email}&maxResults=5",
          headers: [{ key: "Authorization", value: "Bearer {gmail_token}" }],
          variables: [
            {
              name: "messages[0].id",
              customName: "latest_thread_id",
              type: "string",
            },
            {
              name: "messages[0].snippet",
              customName: "latest_snippet",
              type: "string",
            },
            {
              name: "resultSizeEstimate",
              customName: "thread_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI draft generation
      aiNode(
        "4",
        "AI: Generate contextual email draft",
        760,
        "You are an expert business email writer. Draft a professional email based on the context, CRM data, and previous thread history. Match the desired tone. Include a clear subject line. Flag if the content is sensitive (mentions pricing, legal terms, competitor names, or personnel changes). Respond JSON: {subject: string, body: string, is_sensitive: string, sensitivity_reason: string, confidence: number, suggested_send_time: string}",
        "From: {sender_name} ({sender_email}), To: {recipient_first} {recipient_last} ({recipient_email}), Company: {recipient_company}, Title: {recipient_title}, Deal stage: {deal_stage}, Last notes: {last_notes}, Previous thread: {latest_snippet}, Key points: {key_points}, Tone: {desired_tone}",
        [
          { field: "subject", as: "draft_subject", type: "string" },
          { field: "body", as: "draft_body", type: "string" },
          { field: "is_sensitive", as: "is_sensitive", type: "string" },
          {
            field: "sensitivity_reason",
            as: "sensitivity_reason",
            type: "string",
          },
          { field: "confidence", as: "draft_confidence", type: "number" },
          {
            field: "suggested_send_time",
            as: "suggested_send_time",
            type: "string",
          },
        ],
      ),

      // 5. Quality/sensitivity condition
      n("5", "condition", "Sensitive content detected?", 1000, {
        conditionData: [
          {
            variable: "is_sensitive",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Approval gate for sensitive
      n(
        "6",
        "approvalGate",
        "Approval: Review sensitive email draft",
        1240,
        {
          approvalGateData: {
            message:
              "Sensitive email draft requires review before sending.\n\nTo: {recipient_first} {recipient_last} ({recipient_email})\nSubject: {draft_subject}\nSensitivity: {sensitivity_reason}\nConfidence: {draft_confidence}%\n\nDraft body:\n{draft_body}\n\nApprove to create Gmail draft, reject to discard.",
          },
        },
        460,
      ),

      // 7. Gmail draft creation
      n("7", "hitApi", "Create draft in Gmail", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://gmail.googleapis.com/gmail/v1/users/me/drafts",
          headers: [
            { key: "Authorization", value: "Bearer {gmail_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message", value: '{"raw":""}' },
            {
              key: "headers",
              value:
                '{"To":"{recipient_email}","Subject":"{draft_subject}","From":"{sender_email}"}',
            },
            { key: "body", value: "{draft_body}" },
          ],
          variables: [
            { name: "id", customName: "draft_id", type: "string" },
            {
              name: "message.id",
              customName: "gmail_message_id",
              type: "string",
            },
          ],
        },
      }),

      // 8. Learning log
      n("8", "hitApi", "Log draft generation to learning system", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{sender_email}" },
            { key: "event", value: "email_draft_generated" },
            {
              key: "properties",
              value:
                '{"recipient":"{recipient_email}","subject":"{draft_subject}","is_sensitive":"{is_sensitive}","sensitivity_reason":"{sensitivity_reason}","confidence":"{draft_confidence}","tone":"{desired_tone}","deal_stage":"{deal_stage}","draft_id":"{draft_id}"}',
            },
          ],
          variables: [
            { name: "success", customName: "learning_logged", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // sensitive -> approval gate
      e("5", "7", "output_2"), // not sensitive -> direct draft
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Smart Email Drafting Agent** — CRM-enriched, thread-aware AI email drafting with sensitivity screening.\n\n1. Draft request arrives via Slack or internal tool\n2. **CRM enrichment** pulls recipient details, deal stage, and last notes\n3. **Gmail thread retrieval** provides conversation context\n4. **AI generates draft** matching desired tone with CRM and thread context\n5. Sensitivity check: pricing, legal, competitor, or personnel content\n6. Sensitive: **Approval gate** for human review\n7. **Gmail draft created** ready for sender review and send\n8. **Learning log** tracks draft quality, tone, and sensitivity patterns\n\n**To activate:** Say "connect hubspot", "connect gmail", "connect openai", "connect segment", "connect slack"`,
  },

  // ===================================================================
  // 159. SMART NOTIFICATION DIGEST AGENT (8 nodes)
  // ===================================================================

  "Smart Notification Digest Agent": {
    nodes: [
      trigger("Digest schedule: Daily notification compilation", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Daily scheduled digest aggregates notifications from multiple sources, deduplicates, and delivers a prioritized summary",
        setup:
          "Activate this workflow. Configure source webhooks for GitHub, Jira, Slack, and email to feed into the aggregation endpoint.",
        inputFields: [
          { name: "user_email", type: "string", example: "dev@company.com" },
          { name: "user_name", type: "string", example: "Riley Chen" },
          {
            name: "digest_sources",
            type: "string",
            example: "github,jira,slack,email",
          },
        ],
      }),

      // 2. Multi-source aggregation
      n("2", "hitApi", "Aggregate notifications from multiple sources", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.github.com/notifications?since={yesterday_iso}&all=false",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github+json" },
          ],
          variables: [
            {
              name: "[0].subject.title",
              customName: "gh_notif_1",
              type: "string",
            },
            {
              name: "[1].subject.title",
              customName: "gh_notif_2",
              type: "string",
            },
            {
              name: "[2].subject.title",
              customName: "gh_notif_3",
              type: "string",
            },
            { name: "length", customName: "gh_notif_count", type: "number" },
          ],
        },
      }),

      // 3. Normalization + dedup (runCode)
      n("3", "runCode", "Normalize + deduplicate notifications", 520, {
        runCodeData: {
          code: "const gh = [input.gh_notif_1, input.gh_notif_2, input.gh_notif_3].filter(Boolean); const unique = [...new Set(gh)]; output.normalized_items = unique.join(' | '); output.total_notifications = unique.length; output.dedup_count = gh.length - unique.length; output.sources_summary = 'GitHub: ' + unique.length + ' notifications';",
        },
      }),

      // 4. AI classification + grouping
      aiNode(
        "4",
        "AI: Classify, prioritize, and group notifications",
        760,
        "You are a notification triage specialist. Classify each notification by urgency (critical/high/medium/low), group by category (code-review, deployment, bug, feature, discussion), and create a prioritized digest summary. Respond JSON: {critical_items: string, high_items: string, medium_items: string, low_items: string, has_critical: string, digest_summary: string, action_items: string, digest_html: string}",
        "User: {user_name}, Notifications: {normalized_items}, Total: {total_notifications}, Sources: {sources_summary}",
        [
          { field: "critical_items", as: "critical_items", type: "string" },
          { field: "high_items", as: "high_items", type: "string" },
          { field: "medium_items", as: "medium_items", type: "string" },
          { field: "has_critical", as: "has_critical", type: "string" },
          { field: "digest_summary", as: "digest_summary", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "digest_html", as: "digest_html", type: "string" },
        ],
      ),

      // 5. Urgency condition
      n("5", "condition", "Critical items detected?", 1000, {
        conditionData: [
          {
            variable: "has_critical",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Digest generation + critical Slack alert
      slackNode(
        "6",
        "Slack: Critical notification alert",
        1240,
        "*CRITICAL NOTIFICATIONS — Immediate attention required*\n\n{critical_items}\n\nFull digest sent to {user_email}.",
        460,
      ),

      // 7. Channel delivery (email)
      emailNode(
        "7",
        "Daily notification digest email",
        1480,
        "{user_email}",
        "Your daily digest: {total_notifications} notifications ({digest_summary})",
        "<h2>Good morning, {user_name}!</h2><p>Here's your daily notification digest:</p>{digest_html}<h3>Action items:</h3><p>{action_items}</p><p><strong>Critical:</strong> {critical_items}</p><p><strong>High:</strong> {high_items}</p><p><strong>Medium:</strong> {medium_items}</p><p>Deduped {dedup_count} duplicate notifications across sources.</p>",
      ),

      // 8. Engagement learning log
      n("8", "hitApi", "Log digest engagement to analytics", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_email}" },
            { key: "event", value: "digest_delivered" },
            {
              key: "properties",
              value:
                '{"total_notifications":"{total_notifications}","dedup_count":"{dedup_count}","has_critical":"{has_critical}","sources":"{sources_summary}","action_items":"{action_items}"}',
            },
          ],
          variables: [
            { name: "success", customName: "digest_logged", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // critical -> Slack alert
      e("5", "7", "output_2"), // no critical -> email only
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Smart Notification Digest Agent** — Multi-source notification aggregation with AI triage and prioritized delivery.\n\n1. Daily schedule triggers notification compilation\n2. **GitHub aggregation** pulls recent notifications\n3. **RunCode normalizes** and deduplicates across sources\n4. **AI classifies** by urgency, groups by category, creates prioritized digest\n5. Critical items: **Slack alert** for immediate attention\n6. Non-critical: digest delivered via email only\n7. **Digest email** with full prioritized summary and action items\n8. **Learning log** tracks digest patterns and engagement\n\n**To activate:** Say "connect github", "connect openai", "connect email", "connect slack", "connect segment"`,
  },

  // ===================================================================
  // 160. SUBSCRIPTION SERVICE ONBOARDING TO FIRST BOX (9 nodes)
  // ===================================================================

  "Subscription Service Onboarding to First Box": {
    nodes: [
      trigger("Subscription webhook: New subscriber", "webhook_app", {
        source: "Stripe / Chargebee",
        event: "customer.subscription.created",
        howItStarts:
          "A new subscription is created, triggering the onboarding-to-first-box pipeline",
        setup:
          "Connect Stripe webhook for customer.subscription.created event. Include customer email, name, plan, and preferences.",
        inputFields: [
          {
            name: "customer_email",
            type: "string",
            example: "subscriber@example.com",
          },
          { name: "customer_name", type: "string", example: "Avery Mitchell" },
          { name: "plan_name", type: "string", example: "Premium Monthly Box" },
          { name: "subscription_id", type: "string", example: "sub_abc123" },
          {
            name: "preferences",
            type: "string",
            example: "vegan, gluten-free, surprise me",
          },
        ],
      }),

      // 2. CRM sync (HubSpot)
      n("2", "hitApi", "Sync subscriber to HubSpot CRM", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{customer_email}","firstname":"{customer_name}","subscription_plan":"{plan_name}","subscription_id":"{subscription_id}","preferences":"{preferences}","lifecyclestage":"customer","subscription_status":"active"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      // 3. Welcome email
      emailNode(
        "3",
        "Welcome email: Subscription confirmed",
        520,
        "{customer_email}",
        "Welcome to your {plan_name}, {customer_name}!",
        "<h2>Your subscription is live!</h2><p>Hi {customer_name},</p><p>Welcome to your <strong>{plan_name}</strong>! Here's what happens next:</p><ol><li>Complete your taste profile (if you haven't already)</li><li>We'll curate your first box based on your preferences</li><li>You'll get a preview email before we ship</li></ol><p>Your preferences: {preferences}</p><p><a href='https://app.yourbox.com/profile/{customer_email}'>Complete your taste profile</a></p>",
      ),

      // 4. Profile delay + check condition
      n("4", "delayAction", "Wait 48 hours for profile completion", 760, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
        },
      }),

      // 4b. Profile check
      n("4b", "condition", "Taste profile completed?", 1000, {
        conditionData: [
          {
            variable: "preferences",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Profile rescue email
      emailNode(
        "5",
        "Rescue email: Complete your taste profile",
        1240,
        "{customer_email}",
        "{customer_name}, don't forget to set up your taste profile!",
        "<p>Hi {customer_name},</p><p>Your {plan_name} is active but we haven't gotten your full taste profile yet. Without it, we'll use your initial preferences ({preferences}) — but completing the full profile means a much better first box!</p><p><a href='https://app.yourbox.com/profile/{customer_email}'>Complete profile now (2 minutes)</a></p>",
        460,
      ),

      // 6. AI box curation
      aiNode(
        "6",
        "AI: Curate first box based on preferences",
        1480,
        "You are a subscription box curator. Based on the customer preferences and plan tier, select 5-7 items for their first box. Ensure variety, respect dietary restrictions, and include one surprise item. Respond JSON: {box_items: string, box_value: string, surprise_item: string, curation_notes: string, preview_message: string, swap_deadline: string}",
        "Customer: {customer_name}, Plan: {plan_name}, Preferences: {preferences}, Subscription: {subscription_id}",
        [
          { field: "box_items", as: "box_items", type: "string" },
          { field: "box_value", as: "box_value", type: "string" },
          { field: "surprise_item", as: "surprise_item", type: "string" },
          { field: "curation_notes", as: "curation_notes", type: "string" },
          { field: "preview_message", as: "preview_message", type: "string" },
          { field: "swap_deadline", as: "swap_deadline", type: "string" },
        ],
      ),

      // 7. Preview email with swap deadline
      emailNode(
        "7",
        "Preview email: Your first box is ready!",
        1720,
        "{customer_email}",
        "Sneak peek: Your first {plan_name} is curated, {customer_name}!",
        "<h2>Your first box preview</h2><p>Hi {customer_name},</p><p>{preview_message}</p><p><strong>What's inside:</strong></p><p>{box_items}</p><p><strong>Surprise item:</strong> {surprise_item}</p><p>Box value: {box_value}</p><p><strong>Want to swap anything?</strong> You have until {swap_deadline} to make changes.</p><p><a href='https://app.yourbox.com/box/preview/{subscription_id}'>Review & customize your box</a></p>",
      ),

      // 8. Fulfillment handoff
      n("8", "hitApi", "Fulfillment handoff: Queue box for packing", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.shipstation.com/orders/createorder",
          headers: [
            { key: "Authorization", value: "Basic {shipstation_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "orderNumber", value: "{subscription_id}-box1" },
            { key: "orderStatus", value: "awaiting_shipment" },
            { key: "customerEmail", value: "{customer_email}" },
            { key: "items", value: "{box_items}" },
            {
              key: "internalNotes",
              value:
                "First box. Curation: {curation_notes}. Surprise: {surprise_item}",
            },
          ],
          variables: [
            {
              name: "orderId",
              customName: "shipstation_order_id",
              type: "string",
            },
            { name: "orderNumber", customName: "order_number", type: "string" },
          ],
        },
      }),

      // 9. Exception queue (Slack)
      slackNode(
        "9",
        "Slack: First box exception queue",
        2200,
        "*FIRST BOX ONBOARDING COMPLETE*\n\nSubscriber: {customer_name} ({customer_email})\nPlan: {plan_name} | Subscription: {subscription_id}\nBox items: {box_items}\nSurprise: {surprise_item} | Value: {box_value}\nShipStation order: {order_number}\nSwap deadline: {swap_deadline}\n\nMonitor for swap requests before deadline.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "4b"),
      e("4b", "6", "output_1"), // profile done -> curation
      e("4b", "5", "output_2"), // profile not done -> rescue email
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**Subscription Service Onboarding to First Box** — End-to-end subscription onboarding from signup to fulfillment handoff.\n\n1. New subscription triggers via Stripe webhook\n2. **HubSpot CRM sync** creates customer record\n3. **Welcome email** with profile completion CTA\n4. 48-hour wait for taste profile completion\n5. Incomplete? **Rescue email** urges profile setup\n6. **AI curates first box** respecting preferences and dietary restrictions\n7. **Preview email** with box contents and swap deadline\n8. **ShipStation fulfillment** handoff queues box for packing\n9. **Slack exception queue** monitors for swap requests\n\n**To activate:** Say "connect stripe", "connect hubspot", "connect openai", "connect email", "connect shipstation", "connect slack"`,
  },

  // ===================================================================
  // 161. TEXT TO WORKFLOW GENERATOR (8 nodes)
  // ===================================================================

  "Text to Workflow Generator": {
    nodes: [
      trigger("NL request: User describes desired workflow", "webhook_app", {
        source: "Chat / Slack / Internal Tool",
        event: "workflow.generation_requested",
        howItStarts:
          "A user submits a natural language description of a workflow they want to create",
        setup:
          "Connect via Slack slash command, chat widget, or API endpoint. The user provides a text description of the workflow they want.",
        inputFields: [
          {
            name: "user_email",
            type: "string",
            example: "builder@company.com",
          },
          { name: "user_name", type: "string", example: "Casey Park" },
          {
            name: "workflow_description",
            type: "string",
            example:
              "When a new lead comes in from HubSpot, enrich with Clearbit, score them, and route hot leads to Slack",
          },
          {
            name: "available_integrations",
            type: "string",
            example: "hubspot,clearbit,openai,slack,email,twilio",
          },
        ],
      }),

      // 2. AI intent parsing
      aiNode(
        "2",
        "AI: Parse intent + extract requirements",
        280,
        "You are a workflow architect. Parse the natural language workflow description and extract: trigger type, required integrations, node sequence, conditions, and expected outputs. Identify any missing integrations. Respond JSON: {trigger_type: string, required_integrations: string, node_sequence: string, conditions: string, missing_integrations: string, has_missing: string, complexity: string, estimated_nodes: number}",
        "Description: {workflow_description}, Available integrations: {available_integrations}, User: {user_name}",
        [
          { field: "trigger_type", as: "parsed_trigger", type: "string" },
          {
            field: "required_integrations",
            as: "required_integrations",
            type: "string",
          },
          { field: "node_sequence", as: "node_sequence", type: "string" },
          { field: "conditions", as: "parsed_conditions", type: "string" },
          {
            field: "missing_integrations",
            as: "missing_integrations",
            type: "string",
          },
          { field: "has_missing", as: "has_missing", type: "string" },
          { field: "complexity", as: "workflow_complexity", type: "string" },
          { field: "estimated_nodes", as: "estimated_nodes", type: "number" },
        ],
      ),

      // 3. Integration availability check (runCode)
      n("3", "runCode", "Check integration availability", 520, {
        runCodeData: {
          code: "const available = (input.available_integrations || '').split(',').map(s => s.trim()); const required = (input.required_integrations || '').split(',').map(s => s.trim()); const missing = required.filter(r => !available.includes(r)); output.missing_list = missing.join(', '); output.all_available = missing.length === 0; output.availability_report = missing.length === 0 ? 'All integrations available' : 'Missing: ' + missing.join(', ');",
        },
      }),

      // 4. Workflow JSON generation (AI)
      aiNode(
        "4",
        "AI: Generate workflow JSON structure",
        760,
        "You are a workflow JSON generator. Based on the parsed requirements, generate a complete workflow definition with nodes, edges, and configuration. Use proper node types (campaign, hitApi, sendEmail, sendSMS, condition, delayAction, runCode, approvalGate). Respond JSON: {workflow_name: string, workflow_json: string, node_count: number, edge_count: number, summary: string, validation_notes: string}",
        "Trigger: {parsed_trigger}, Node sequence: {node_sequence}, Conditions: {parsed_conditions}, Complexity: {workflow_complexity}, Available integrations: {available_integrations}, Missing: {missing_list}",
        [
          { field: "workflow_name", as: "generated_name", type: "string" },
          { field: "workflow_json", as: "generated_json", type: "string" },
          { field: "node_count", as: "node_count", type: "number" },
          { field: "edge_count", as: "edge_count", type: "number" },
          { field: "summary", as: "generated_summary", type: "string" },
          { field: "validation_notes", as: "validation_notes", type: "string" },
        ],
      ),

      // 5. Missing integration condition
      n("5", "condition", "All integrations available?", 1000, {
        conditionData: [
          {
            variable: "all_available",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Structural validation (runCode)
      n("6", "runCode", "Validate workflow structure + edges", 1240, {
        runCodeData: {
          code: "try { const wf = JSON.parse(input.generated_json || '{}'); const hasNodes = wf.nodes && wf.nodes.length > 0; const hasEdges = wf.edges && wf.edges.length > 0; const hasTrigger = hasNodes && wf.nodes.some(n => n.type === 'campaign'); output.is_valid = hasNodes && hasEdges && hasTrigger; output.validation_result = output.is_valid ? 'Valid workflow structure' : 'Invalid: ' + (!hasNodes ? 'no nodes' : !hasEdges ? 'no edges' : 'no trigger'); output.node_types = hasNodes ? wf.nodes.map(n => n.type).join(',') : 'none'; } catch(e) { output.is_valid = false; output.validation_result = 'JSON parse error: ' + e.message; output.node_types = 'error'; }",
        },
      }),

      // 7. Preview + approval gate
      n("7", "approvalGate", "Preview + approve generated workflow", 1480, {
        approvalGateData: {
          message:
            "Generated workflow: {generated_name}\n\nDescription: {workflow_description}\nNodes: {node_count} | Edges: {edge_count}\nComplexity: {workflow_complexity}\nValidation: {validation_result}\n\nSummary: {generated_summary}\nNotes: {validation_notes}\n\nApprove to deploy, reject to discard.",
        },
      }),

      // 8. Deployment + logging
      n("8", "hitApi", "Deploy workflow + log generation event", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_email}" },
            { key: "event", value: "workflow_generated_and_deployed" },
            {
              key: "properties",
              value:
                '{"workflow_name":"{generated_name}","description":"{workflow_description}","node_count":"{node_count}","complexity":"{workflow_complexity}","is_valid":"{is_valid}","missing_integrations":"{missing_list}","user":"{user_name}"}',
            },
          ],
          variables: [
            {
              name: "success",
              customName: "deployment_logged",
              type: "boolean",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // all available -> validate
      e("5", "7", "output_2"), // missing -> still show preview with warning
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Text to Workflow Generator** — Natural language to workflow pipeline with AI parsing, validation, and deployment.\n\n1. User submits natural language workflow description\n2. **AI parses intent** extracting trigger, integrations, node sequence, and conditions\n3. **RunCode checks** integration availability against connected services\n4. **AI generates** complete workflow JSON with proper node types and edges\n5. Missing integration check: warns if required services aren't connected\n6. **RunCode validates** structural integrity (nodes, edges, trigger present)\n7. **Approval gate** for human review before deployment\n8. **Deployment logging** tracks generation patterns and success rates\n\n**To activate:** Say "connect openai", "connect segment"`,
  },

  // ===================================================================
  // 162. TRIAL ENDED GRACE-PERIOD RESCUE (8 nodes)
  // ===================================================================

  "Trial Ended Grace-Period Rescue": {
    nodes: [
      trigger("Trial expired: Grace period begins", "webhook_app", {
        source: "Stripe / Internal Billing",
        event: "customer.subscription.trial_will_end",
        howItStarts:
          "A trial subscription expires and a grace period begins for conversion rescue",
        setup:
          "Connect Stripe webhook for trial_will_end event or internal billing system. Include user email, name, plan, trial duration, and phone.",
        inputFields: [
          {
            name: "user_email",
            type: "string",
            example: "trialist@company.com",
          },
          { name: "user_name", type: "string", example: "Drew Kim" },
          { name: "user_phone", type: "string", example: "+15551239876" },
          { name: "plan_name", type: "string", example: "Pro Plan" },
          { name: "trial_days", type: "string", example: "14" },
          { name: "grace_period_end", type: "string", example: "2024-02-01" },
        ],
      }),

      // 2. Engagement pull (Mixpanel)
      n("2", "hitApi", "Pull trial engagement from Mixpanel", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://mixpanel.com/api/2.0/engage?project_id={mixpanel_project_id}",
          headers: [
            { key: "Authorization", value: "Basic {mixpanel_service_account}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "filter_by_cohort", value: '{"id":"{user_email}"}' },
            {
              key: "output_properties",
              value:
                '["total_sessions","features_used","last_active","peak_usage_day","key_actions_completed"]',
            },
          ],
          variables: [
            {
              name: "results[0].$properties.total_sessions",
              customName: "trial_sessions",
              type: "number",
            },
            {
              name: "results[0].$properties.features_used",
              customName: "features_used",
              type: "string",
            },
            {
              name: "results[0].$properties.last_active",
              customName: "last_active",
              type: "string",
            },
            {
              name: "results[0].$properties.peak_usage_day",
              customName: "peak_usage_day",
              type: "string",
            },
            {
              name: "results[0].$properties.key_actions_completed",
              customName: "key_actions_completed",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI conversion likelihood scoring
      aiNode(
        "3",
        "AI: Score conversion likelihood + craft rescue",
        520,
        "You are a trial conversion analyst. Score this trialist conversion likelihood (0-100) based on their engagement during the trial. Craft a rescue email for the grace period. Respond JSON: {conversion_score: number, engagement_tier: string, rescue_subject: string, rescue_body: string, sms_countdown: string, objection_hypothesis: string, recommended_offer: string}",
        "User: {user_name} ({user_email}), Plan: {plan_name}, Trial days: {trial_days}, Grace period ends: {grace_period_end}, Sessions: {trial_sessions}, Features used: {features_used}, Last active: {last_active}, Peak day: {peak_usage_day}, Key actions: {key_actions_completed}",
        [
          { field: "conversion_score", as: "conversion_score", type: "number" },
          { field: "engagement_tier", as: "engagement_tier", type: "string" },
          { field: "rescue_subject", as: "rescue_subject", type: "string" },
          { field: "rescue_body", as: "rescue_body", type: "string" },
          { field: "sms_countdown", as: "sms_countdown", type: "string" },
          {
            field: "objection_hypothesis",
            as: "objection_hypothesis",
            type: "string",
          },
          {
            field: "recommended_offer",
            as: "recommended_offer",
            type: "string",
          },
        ],
      ),

      // 4. Engagement tier condition
      n("4", "condition", "Engaged trialist? (score > 40)", 760, {
        conditionData: [
          {
            variable: "conversion_score",
            value: "40",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Grace period rescue email
      emailNode(
        "5",
        "Grace period rescue email",
        1000,
        "{user_email}",
        "{rescue_subject}",
        "<p>Hi {user_name},</p><p>{rescue_body}</p><p>Your {plan_name} trial has ended, but you still have access until <strong>{grace_period_end}</strong>.</p><p>During your trial, you used {features_used} and completed {key_actions_completed} key actions across {trial_sessions} sessions.</p><p>Recommended: {recommended_offer}</p><p><a href='https://app.yourproduct.com/billing/upgrade?plan={plan_name}'>Upgrade now to keep your data</a></p>",
      ),

      // 6. SMS countdown via Twilio
      n("6", "sendSMS", "SMS: Grace period countdown", 1240, {
        sendSmsDetails: {
          to: "{user_phone}",
          message:
            "{sms_countdown} Your {plan_name} grace period ends {grace_period_end}. Upgrade to keep your work: https://app.yourproduct.com/billing/upgrade",
        },
      }),

      // 7. Grace period end check condition
      n("7", "delayAction", "Wait until grace period nears end", 1480, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
        },
      }),

      // 8. Non-conversion CRM update
      n("8", "hitApi", "CRM update: Trial conversion outcome", 1720, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"trial_conversion_score":"{conversion_score}","engagement_tier":"{engagement_tier}","trial_sessions":"{trial_sessions}","features_used":"{features_used}","objection_hypothesis":"{objection_hypothesis}","grace_period_end":"{grace_period_end}","recommended_offer":"{recommended_offer}","trial_outcome":"grace_period_expired"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_update_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // engaged -> rescue email
      e("4", "8", "output_2"), // not engaged -> CRM update only
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Trial Ended Grace-Period Rescue** — AI-scored conversion likelihood with tiered rescue for expired trials.\n\n1. Trial expiration triggers grace period pipeline\n2. **Mixpanel engagement pull** retrieves session count, features used, and key actions\n3. **AI scores conversion likelihood** and crafts personalized rescue messaging\n4. Engagement tier routing: engaged users get full rescue treatment\n5. **Grace period rescue email** with usage stats and upgrade CTA\n6. **SMS countdown** via Twilio with upgrade link\n7. Wait until grace period nears end\n8. **CRM update** records conversion score, objections, and outcome\n\n**To activate:** Say "connect stripe", "connect mixpanel", "connect openai", "connect email", "connect twilio", "connect hubspot"`,
  },

  // ===================================================================
  // 163. TRIAL ENDING SOON (7 nodes)
  // ===================================================================

  "Trial Ending Soon": {
    nodes: [
      trigger("Near-expiry scan: Trials ending in 3 days", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Daily scan identifies trial users whose trial ends within 3 days to send conversion nudges",
        setup:
          "Activate this workflow. It runs daily to find trials expiring in the next 3 days.",
        inputFields: [
          {
            name: "user_email",
            type: "string",
            example: "trialist@startup.com",
          },
          { name: "user_name", type: "string", example: "Quinn Anderson" },
          { name: "plan_name", type: "string", example: "Growth Plan" },
          { name: "trial_end_date", type: "string", example: "2024-01-20" },
          { name: "days_remaining", type: "string", example: "3" },
        ],
      }),

      // 2. Usage summary pull
      n("2", "hitApi", "Pull usage summary from product analytics", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://mixpanel.com/api/2.0/engage?project_id={mixpanel_project_id}",
          headers: [
            { key: "Authorization", value: "Basic {mixpanel_service_account}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "filter_by_cohort", value: '{"id":"{user_email}"}' },
            {
              key: "output_properties",
              value:
                '["total_sessions","features_activated","automations_created","contacts_imported","emails_sent","last_login"]',
            },
          ],
          variables: [
            {
              name: "results[0].$properties.total_sessions",
              customName: "total_sessions",
              type: "number",
            },
            {
              name: "results[0].$properties.features_activated",
              customName: "features_activated",
              type: "string",
            },
            {
              name: "results[0].$properties.automations_created",
              customName: "automations_created",
              type: "number",
            },
            {
              name: "results[0].$properties.contacts_imported",
              customName: "contacts_imported",
              type: "number",
            },
            {
              name: "results[0].$properties.emails_sent",
              customName: "emails_sent",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI personalized conversion pitch
      aiNode(
        "3",
        "AI: Personalized conversion pitch",
        520,
        "You are a SaaS conversion specialist. Based on this user's trial usage, craft a personalized conversion pitch that emphasizes the value they have already created and what they would lose. Reference specific features and metrics. Respond JSON: {pitch_subject: string, pitch_body: string, value_created_summary: string, loss_aversion_hook: string, recommended_plan: string, is_expiry_day: string}",
        "User: {user_name} ({user_email}), Plan: {plan_name}, Trial ends: {trial_end_date}, Days left: {days_remaining}, Sessions: {total_sessions}, Features: {features_activated}, Automations: {automations_created}, Contacts: {contacts_imported}, Emails sent: {emails_sent}",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
          {
            field: "value_created_summary",
            as: "value_created",
            type: "string",
          },
          { field: "loss_aversion_hook", as: "loss_hook", type: "string" },
          { field: "recommended_plan", as: "recommended_plan", type: "string" },
          { field: "is_expiry_day", as: "is_expiry_day", type: "string" },
        ],
      ),

      // 4. Trial ending email
      emailNode(
        "4",
        "Trial ending email: Conversion pitch",
        760,
        "{user_email}",
        "{pitch_subject}",
        "<p>Hi {user_name},</p><p>{pitch_body}</p><p><strong>What you've built so far:</strong> {value_created}</p><p>{loss_hook}</p><p>We recommend the <strong>{recommended_plan}</strong> for your needs.</p><p><a href='https://app.yourproduct.com/billing/upgrade?plan={recommended_plan}'>Upgrade before {trial_end_date}</a></p>",
      ),

      // 5. In-app banner creation
      n("5", "hitApi", "Create in-app conversion banner", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourproduct.com/v1/banners",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_email", value: "{user_email}" },
            { key: "banner_type", value: "trial_ending" },
            {
              key: "message",
              value: "{loss_hook} Your trial ends {trial_end_date}.",
            },
            { key: "cta_text", value: "Upgrade to {recommended_plan}" },
            {
              key: "cta_url",
              value: "/billing/upgrade?plan={recommended_plan}",
            },
            { key: "expires_at", value: "{trial_end_date}" },
          ],
          variables: [
            { name: "banner_id", customName: "banner_id", type: "string" },
          ],
        },
      }),

      // 6. Expiry day condition
      n("6", "condition", "Is this the expiry day?", 1240, {
        conditionData: [
          {
            variable: "is_expiry_day",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Sales Slack alert
      slackNode(
        "7",
        "Slack: Sales alert for expiring trial",
        1480,
        "*TRIAL EXPIRING TODAY*\n\nUser: {user_name} ({user_email})\nPlan: {plan_name} | Ends: {trial_end_date}\nSessions: {total_sessions} | Automations: {automations_created}\nContacts: {contacts_imported} | Emails sent: {emails_sent}\nFeatures: {features_activated}\n\nValue created: {value_created}\nRecommended plan: {recommended_plan}\n\nReach out NOW for last-day conversion.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // expiry day -> sales alert
    ],
    summary: `**Trial Ending Soon** — AI-powered conversion pitch with usage-based loss aversion for expiring trials.\n\n1. Daily scan finds trials ending within 3 days\n2. **Product analytics** pull retrieves sessions, features, automations, and contacts\n3. **AI crafts conversion pitch** emphasizing value already created and potential loss\n4. **Trial ending email** with personalized metrics and upgrade CTA\n5. **In-app banner** created with countdown and upgrade button\n6. On expiry day: **Sales Slack alert** for immediate outreach\n\n**To activate:** Say "connect mixpanel", "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 164. TRIAL LOGIN NO FIRST KEY ACTION (8 nodes)
  // ===================================================================

  "Trial Login No First Key Action": {
    nodes: [
      trigger("No-key-action: Trial user logged in but stuck", "webhook_app", {
        source: "Segment / Amplitude",
        event: "trial.no_key_action",
        howItStarts:
          "A trial user has logged in 3+ times but has not completed their first key action (e.g., create first workflow, import contacts)",
        setup:
          "Configure Segment to fire when a trial user has 3+ sessions but zero key_action_completed events. Include user details and session data.",
        inputFields: [
          { name: "user_email", type: "string", example: "stuck@trial.com" },
          { name: "user_name", type: "string", example: "Hayden Foster" },
          { name: "user_phone", type: "string", example: "+15556667777" },
          { name: "login_count", type: "string", example: "5" },
          {
            name: "expected_key_action",
            type: "string",
            example: "create_first_workflow",
          },
        ],
      }),

      // 2. Session context pull
      n("2", "hitApi", "Pull session context from Mixpanel", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://mixpanel.com/api/2.0/engage?project_id={mixpanel_project_id}",
          headers: [
            { key: "Authorization", value: "Basic {mixpanel_service_account}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "filter_by_cohort", value: '{"id":"{user_email}"}' },
            {
              key: "output_properties",
              value:
                '["pages_viewed","time_on_site","clicks","errors_encountered","help_docs_viewed","device_type"]',
            },
          ],
          variables: [
            {
              name: "results[0].$properties.pages_viewed",
              customName: "pages_viewed",
              type: "string",
            },
            {
              name: "results[0].$properties.time_on_site",
              customName: "time_on_site",
              type: "number",
            },
            {
              name: "results[0].$properties.errors_encountered",
              customName: "errors_encountered",
              type: "number",
            },
            {
              name: "results[0].$properties.help_docs_viewed",
              customName: "help_docs_viewed",
              type: "number",
            },
            {
              name: "results[0].$properties.device_type",
              customName: "device_type",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI blocker hypothesis
      aiNode(
        "3",
        "AI: Diagnose blocker + craft guided help",
        520,
        "You are a product activation specialist. Analyze why this trial user keeps logging in but never completes their first key action. Hypothesize the blocker based on pages viewed, errors, and time spent. Craft a helpful email and SMS. Respond JSON: {blocker_hypothesis: string, help_subject: string, help_body: string, deep_link_url: string, sms_walkthrough: string, friction_category: string, has_completed: string}",
        "User: {user_name} ({user_email}), Logins: {login_count}, Expected action: {expected_key_action}, Pages: {pages_viewed}, Time on site: {time_on_site}min, Errors: {errors_encountered}, Help docs viewed: {help_docs_viewed}, Device: {device_type}",
        [
          {
            field: "blocker_hypothesis",
            as: "blocker_hypothesis",
            type: "string",
          },
          { field: "help_subject", as: "help_subject", type: "string" },
          { field: "help_body", as: "help_body", type: "string" },
          { field: "deep_link_url", as: "deep_link_url", type: "string" },
          { field: "sms_walkthrough", as: "sms_walkthrough", type: "string" },
          {
            field: "friction_category",
            as: "friction_category",
            type: "string",
          },
          { field: "has_completed", as: "has_completed", type: "string" },
        ],
      ),

      // 4. Guided help email with deep link
      emailNode(
        "4",
        "Guided help email: Complete your first action",
        760,
        "{user_email}",
        "{help_subject}",
        "<p>Hi {user_name},</p><p>{help_body}</p><p>We noticed you've logged in {login_count} times but haven't completed your first <strong>{expected_key_action}</strong> yet. Let us help!</p><p><a href='{deep_link_url}'>Start your first {expected_key_action} now (guided walkthrough)</a></p><p>Or reply to this email — our team can walk you through it live.</p>",
      ),

      // 5. Delay + recheck condition
      n("5", "delayAction", "Wait 48 hours for key action", 1000, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
        },
      }),

      // 5b. Completion check
      n("5b", "condition", "Key action completed after help?", 1240, {
        conditionData: [
          {
            variable: "has_completed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. SMS walkthrough offer (Twilio)
      n("6", "sendSMS", "SMS: Live walkthrough offer", 1480, {
        sendSmsDetails: {
          to: "{user_phone}",
          message:
            "{sms_walkthrough} Need a hand? Reply YES for a free 10-min walkthrough of {expected_key_action}: {deep_link_url}",
        },
      }),

      // 7. CSM Slack alert
      slackNode(
        "7",
        "Slack: CSM alert for stuck trial user",
        1720,
        "*TRIAL USER STUCK — No key action after {login_count} logins*\n\nUser: {user_name} ({user_email})\nExpected action: {expected_key_action}\nBlocker hypothesis: {blocker_hypothesis}\nFriction category: {friction_category}\nErrors encountered: {errors_encountered}\nHelp docs viewed: {help_docs_viewed}\nDevice: {device_type}\n\nEmail + SMS sent. May need human intervention.",
      ),

      // 8. Friction logging
      n("8", "hitApi", "Log friction event to analytics", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_email}" },
            { key: "event", value: "trial_key_action_friction" },
            {
              key: "properties",
              value:
                '{"expected_action":"{expected_key_action}","login_count":"{login_count}","blocker_hypothesis":"{blocker_hypothesis}","friction_category":"{friction_category}","errors":"{errors_encountered}","device":"{device_type}","help_docs_viewed":"{help_docs_viewed}"}',
            },
          ],
          variables: [
            { name: "success", customName: "friction_logged", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "5b"),
      e("5b", "8", "output_1"), // completed -> log only
      e("5b", "6", "output_2"), // not completed -> SMS rescue
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Trial Login No First Key Action** — AI-powered blocker diagnosis for trial users who log in but never activate.\n\n1. Stuck trial user detected (3+ logins, no key action)\n2. **Mixpanel context** pulls pages viewed, errors, and help docs accessed\n3. **AI diagnoses blocker** and hypothesizes friction category\n4. **Guided help email** with deep link to the specific action\n5. 48-hour wait for natural completion\n6. Still stuck? **SMS walkthrough offer** via Twilio\n7. **CSM Slack alert** for human intervention\n8. **Friction logging** tracks blocker patterns\n\n**To activate:** Say "connect mixpanel", "connect openai", "connect email", "connect twilio", "connect slack", "connect segment"`,
  },

  // ===================================================================
  // 165. TRIAL USER COMPLETED ACTION 1 NOT ACTION 2 (8 nodes)
  // ===================================================================

  "Trial User Completed Action 1 Not Action 2": {
    nodes: [
      trigger("Action gap: Completed step 1, stuck on step 2", "webhook_app", {
        source: "Segment / Amplitude",
        event: "trial.action_gap",
        howItStarts:
          "A trial user completed the first activation action but has not progressed to the second key action",
        setup:
          "Configure Segment to fire when a user has action_1_completed=true and action_2_completed=false for more than 48 hours. Include both action names and user context.",
        inputFields: [
          { name: "user_email", type: "string", example: "user@trial.com" },
          { name: "user_name", type: "string", example: "Peyton James" },
          { name: "user_phone", type: "string", example: "+15554443333" },
          {
            name: "action_1_name",
            type: "string",
            example: "Create first workflow",
          },
          {
            name: "action_2_name",
            type: "string",
            example: "Send first campaign",
          },
          { name: "action_1_date", type: "string", example: "2024-01-15" },
        ],
      }),

      // 2. Progress state pull
      n("2", "hitApi", "Pull progress state from product analytics", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://mixpanel.com/api/2.0/engage?project_id={mixpanel_project_id}",
          headers: [
            { key: "Authorization", value: "Basic {mixpanel_service_account}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "filter_by_cohort", value: '{"id":"{user_email}"}' },
            {
              key: "output_properties",
              value:
                '["onboarding_step","action_2_attempts","last_action_2_page","errors_on_action_2","time_on_action_2","sessions_since_action_1"]',
            },
          ],
          variables: [
            {
              name: "results[0].$properties.onboarding_step",
              customName: "current_step",
              type: "string",
            },
            {
              name: "results[0].$properties.action_2_attempts",
              customName: "action_2_attempts",
              type: "number",
            },
            {
              name: "results[0].$properties.last_action_2_page",
              customName: "last_page_visited",
              type: "string",
            },
            {
              name: "results[0].$properties.errors_on_action_2",
              customName: "errors_on_action_2",
              type: "number",
            },
            {
              name: "results[0].$properties.sessions_since_action_1",
              customName: "sessions_since",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI blocker inference
      aiNode(
        "3",
        "AI: Infer blocker between action 1 and 2",
        520,
        "You are an activation funnel specialist. Analyze why a user completed action 1 but is stuck before action 2. Consider their attempts, errors, and pages visited. Craft a bridge email and tooltip suggestion. Respond JSON: {blocker_inference: string, bridge_subject: string, bridge_body: string, walkthrough_url: string, tooltip_text: string, tooltip_target: string, sms_help: string, has_completed_action_2: string}",
        "User: {user_name}, Action 1: {action_1_name} (done {action_1_date}), Action 2: {action_2_name} (NOT done), Current step: {current_step}, Attempts at action 2: {action_2_attempts}, Last page: {last_page_visited}, Errors: {errors_on_action_2}, Sessions since action 1: {sessions_since}",
        [
          {
            field: "blocker_inference",
            as: "blocker_inference",
            type: "string",
          },
          { field: "bridge_subject", as: "bridge_subject", type: "string" },
          { field: "bridge_body", as: "bridge_body", type: "string" },
          { field: "walkthrough_url", as: "walkthrough_url", type: "string" },
          { field: "tooltip_text", as: "tooltip_text", type: "string" },
          { field: "tooltip_target", as: "tooltip_target", type: "string" },
          { field: "sms_help", as: "sms_help", type: "string" },
          {
            field: "has_completed_action_2",
            as: "has_completed_action_2",
            type: "string",
          },
        ],
      ),

      // 4. Bridge email with walkthrough
      emailNode(
        "4",
        "Bridge email: Guide from action 1 to action 2",
        760,
        "{user_email}",
        "{bridge_subject}",
        "<p>Hi {user_name},</p><p>{bridge_body}</p><p>You've already nailed <strong>{action_1_name}</strong> — now let's get you to <strong>{action_2_name}</strong>!</p><p><a href='{walkthrough_url}'>Follow this guided walkthrough</a></p><p>Most users complete this in under 5 minutes.</p>",
      ),

      // 5. In-app tooltip setup
      n("5", "hitApi", "Set up in-app contextual tooltip", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourproduct.com/v1/tooltips",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_email", value: "{user_email}" },
            { key: "tooltip_target", value: "{tooltip_target}" },
            { key: "tooltip_text", value: "{tooltip_text}" },
            { key: "action_name", value: "{action_2_name}" },
            { key: "dismiss_after_action", value: "true" },
          ],
          variables: [
            { name: "tooltip_id", customName: "tooltip_id", type: "string" },
          ],
        },
      }),

      // 6. Delay + recheck condition
      n("6", "delayAction", "Wait 72 hours for action 2 completion", 1240, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 72,
          delayUnit: "hours",
        },
      }),

      // 6b. Completion check
      n("6b", "condition", "Action 2 completed after bridge?", 1480, {
        conditionData: [
          {
            variable: "has_completed_action_2",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. SMS help offer (Twilio)
      n("7", "sendSMS", "SMS: Offer live help for action 2", 1720, {
        sendSmsDetails: {
          to: "{user_phone}",
          message:
            "{sms_help} Need help with {action_2_name}? Reply YES for a free 5-min walkthrough: {walkthrough_url}",
        },
      }),

      // 8. CS Slack alert
      slackNode(
        "8",
        "Slack: CS alert for action gap user",
        1960,
        "*ACTION GAP — Trial user stuck between steps*\n\nUser: {user_name} ({user_email})\nCompleted: {action_1_name} ({action_1_date})\nStuck on: {action_2_name}\nBlocker: {blocker_inference}\nAttempts: {action_2_attempts} | Errors: {errors_on_action_2}\nSessions since action 1: {sessions_since}\n\nEmail + tooltip + SMS sent. Consider live outreach.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "6b"),
      e("6b", "8", "output_1"), // completed -> still alert CS
      e("6b", "7", "output_2"), // not completed -> SMS help
      e("7", "8"),
    ],
    summary: `**Trial User Completed Action 1 Not Action 2** — AI-powered funnel gap analysis with multi-channel bridge interventions.\n\n1. Action gap detected: step 1 done, step 2 stuck for 48+ hours\n2. **Mixpanel pull** retrieves attempts, errors, and pages visited for action 2\n3. **AI infers blocker** between actions and crafts bridge content\n4. **Bridge email** with guided walkthrough link\n5. **In-app tooltip** placed on the exact UI element for action 2\n6. 72-hour wait for natural completion\n7. Still stuck? **SMS help offer** via Twilio\n8. **CS Slack alert** with full context for potential live outreach\n\n**To activate:** Say "connect mixpanel", "connect openai", "connect email", "connect twilio", "connect slack"`,
  },

  // ===================================================================
  // 166. TRIAL-EXPIRED WINBACK (9 nodes)
  // ===================================================================

  "Trial-Expired Winback": {
    nodes: [
      trigger("Expired trial scan: Winback candidates", "schedule", {
        schedule: "Every Monday at 10:00 AM",
        howItStarts:
          "Weekly scan identifies recently expired trials (7-30 days ago) that are candidates for winback campaigns",
        setup:
          "Activate this workflow. It runs weekly to find expired trials within the winback window.",
        inputFields: [
          { name: "user_email", type: "string", example: "lapsed@company.com" },
          { name: "user_name", type: "string", example: "Reese Taylor" },
          { name: "user_phone", type: "string", example: "+15558889999" },
          { name: "plan_name", type: "string", example: "Business Plan" },
          { name: "trial_ended_date", type: "string", example: "2024-01-10" },
          { name: "days_since_expiry", type: "string", example: "14" },
        ],
      }),

      // 2. Historical engagement pull
      n("2", "hitApi", "Pull historical trial engagement", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://mixpanel.com/api/2.0/engage?project_id={mixpanel_project_id}",
          headers: [
            { key: "Authorization", value: "Basic {mixpanel_service_account}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "filter_by_cohort", value: '{"id":"{user_email}"}' },
            {
              key: "output_properties",
              value:
                '["total_sessions","peak_feature","automations_created","contacts_imported","last_login","trial_nps"]',
            },
          ],
          variables: [
            {
              name: "results[0].$properties.total_sessions",
              customName: "trial_sessions",
              type: "number",
            },
            {
              name: "results[0].$properties.peak_feature",
              customName: "peak_feature",
              type: "string",
            },
            {
              name: "results[0].$properties.automations_created",
              customName: "automations_created",
              type: "number",
            },
            {
              name: "results[0].$properties.contacts_imported",
              customName: "contacts_imported",
              type: "number",
            },
            {
              name: "results[0].$properties.trial_nps",
              customName: "trial_nps",
              type: "number",
            },
          ],
        },
      }),

      // 3. Support history pull
      n("3", "hitApi", "Pull support ticket history from Zendesk", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket+requester:{user_email}&sort_by=created_at&sort_order=desc",
          headers: [{ key: "Authorization", value: "Basic {zendesk_auth}" }],
          variables: [
            {
              name: "count",
              customName: "support_ticket_count",
              type: "number",
            },
            {
              name: "results[0].subject",
              customName: "last_ticket_subject",
              type: "string",
            },
            {
              name: "results[0].status",
              customName: "last_ticket_status",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI winback strategy
      aiNode(
        "4",
        "AI: Craft winback strategy + multi-touch plan",
        760,
        "You are a winback campaign strategist. Based on trial engagement, NPS, and support history, craft a personalized winback strategy. Determine engagement tier and appropriate offer level. Respond JSON: {engagement_tier: string, winback_subject: string, winback_body: string, coupon_pct: number, coupon_code: string, sms_lastday: string, winback_strategy: string, is_high_engagement: string}",
        "User: {user_name} ({user_email}), Plan: {plan_name}, Trial ended: {trial_ended_date}, Days since: {days_since_expiry}, Sessions: {trial_sessions}, Peak feature: {peak_feature}, Automations: {automations_created}, Contacts: {contacts_imported}, NPS: {trial_nps}, Support tickets: {support_ticket_count}, Last ticket: {last_ticket_subject} ({last_ticket_status})",
        [
          { field: "engagement_tier", as: "engagement_tier", type: "string" },
          { field: "winback_subject", as: "winback_subject", type: "string" },
          { field: "winback_body", as: "winback_body", type: "string" },
          { field: "coupon_pct", as: "coupon_pct", type: "number" },
          { field: "coupon_code", as: "coupon_code", type: "string" },
          { field: "sms_lastday", as: "sms_lastday", type: "string" },
          { field: "winback_strategy", as: "winback_strategy", type: "string" },
          {
            field: "is_high_engagement",
            as: "is_high_engagement",
            type: "string",
          },
        ],
      ),

      // 5. Engagement tier condition
      n("5", "condition", "High-engagement expired trial?", 1000, {
        conditionData: [
          {
            variable: "is_high_engagement",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Multi-touch email sequence
      emailNode(
        "6",
        "Winback email: Personalized re-engagement",
        1240,
        "{user_email}",
        "{winback_subject}",
        "<p>Hi {user_name},</p><p>{winback_body}</p><p>During your trial, you created {automations_created} automations and imported {contacts_imported} contacts using {peak_feature}. That data is still waiting for you.</p><p>Use code <strong>{coupon_code}</strong> for {coupon_pct}% off your first month of {plan_name}.</p><p><a href='https://app.yourproduct.com/billing/reactivate?code={coupon_code}&plan={plan_name}'>Reactivate your account</a></p>",
      ),

      // 7. Coupon generation (runCode)
      n("7", "runCode", "Generate unique coupon code", 1480, {
        runCodeData: {
          code: "const base = input.coupon_code || 'WINBACK'; const unique = base + '_' + Date.now().toString(36).toUpperCase(); output.unique_coupon = unique; output.coupon_value = input.coupon_pct + '% off first month'; output.coupon_expiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];",
        },
      }),

      // 8. Last-day SMS (Twilio)
      n("8", "sendSMS", "SMS: Last day coupon reminder", 1720, {
        sendSmsDetails: {
          to: "{user_phone}",
          message:
            "{sms_lastday} Your {coupon_pct}% off code {unique_coupon} expires {coupon_expiry}. Reactivate: https://app.yourproduct.com/billing/reactivate?code={unique_coupon}",
        },
      }),

      // 9. CRM lifecycle update
      n("9", "hitApi", "CRM: Update lifecycle to winback", 1960, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"lifecyclestage":"opportunity","winback_tier":"{engagement_tier}","winback_strategy":"{winback_strategy}","winback_coupon":"{unique_coupon}","coupon_pct":"{coupon_pct}","trial_sessions":"{trial_sessions}","peak_feature":"{peak_feature}","support_tickets":"{support_ticket_count}","winback_date":"{current_date}"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_winback_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high engagement -> winback email
      e("5", "9", "output_2"), // low engagement -> just CRM update
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**Trial-Expired Winback** — Multi-source intelligence winback with tiered offers and coupon generation.\n\n1. Weekly scan identifies expired trials within winback window\n2. **Mixpanel pull** retrieves trial engagement: sessions, peak feature, NPS\n3. **Zendesk pull** checks support ticket history for unresolved issues\n4. **AI crafts winback strategy** with engagement-tiered offer levels\n5. Engagement tier routing: high-engagement gets full treatment\n6. **Winback email** with usage-based value reminder and coupon\n7. **RunCode generates** unique coupon code with expiry\n8. **Last-day SMS** reminder via Twilio before coupon expires\n9. **CRM lifecycle update** to winback stage with full context\n\n**To activate:** Say "connect mixpanel", "connect zendesk", "connect openai", "connect email", "connect twilio", "connect hubspot"`,
  },

  // ===================================================================
  // 167. UPGRADE TO HIGHER PLAN BEFORE RENEWAL (8 nodes)
  // ===================================================================

  "Upgrade to Higher Plan Before Renewal": {
    nodes: [
      trigger("Renewal window scan: Customers nearing limits", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Weekly scan identifies customers approaching their plan limits within 30 days of renewal — prime upgrade candidates",
        setup:
          "Activate this workflow. It runs weekly to find customers at 80%+ of plan limits with renewal within 30 days.",
        inputFields: [
          {
            name: "customer_email",
            type: "string",
            example: "growing@client.com",
          },
          { name: "customer_name", type: "string", example: "Dakota Lee" },
          { name: "account_id", type: "string", example: "acct_upgrade01" },
          { name: "current_plan", type: "string", example: "Starter" },
          { name: "renewal_date", type: "string", example: "2024-02-15" },
        ],
      }),

      // 2. Usage + limit pull
      n("2", "hitApi", "Pull usage vs. plan limits from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/subscription_items/{subscription_item_id}/usage_record_summaries?limit=1",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          variables: [
            {
              name: "data[0].total_usage",
              customName: "current_usage",
              type: "number",
            },
            {
              name: "data[0].period.start",
              customName: "period_start",
              type: "number",
            },
            {
              name: "data[0].period.end",
              customName: "period_end",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI upgrade fit scoring
      aiNode(
        "3",
        "AI: Score upgrade fit + personalize pitch",
        520,
        "You are a revenue expansion analyst. Score this customer upgrade fit (0-100) based on usage patterns relative to plan limits, growth trajectory, and renewal timing. Recommend the best upgrade plan and craft a personalized pitch. Respond JSON: {upgrade_score: number, recommended_plan: string, monthly_savings_if_upgrade: string, upgrade_subject: string, upgrade_body: string, upsell_prompt: string, fit_above_threshold: string}",
        "Customer: {customer_name} ({customer_email}), Current plan: {current_plan}, Usage: {current_usage}, Renewal: {renewal_date}, Account: {account_id}",
        [
          { field: "upgrade_score", as: "upgrade_score", type: "number" },
          { field: "recommended_plan", as: "recommended_plan", type: "string" },
          {
            field: "monthly_savings_if_upgrade",
            as: "monthly_savings",
            type: "string",
          },
          { field: "upgrade_subject", as: "upgrade_subject", type: "string" },
          { field: "upgrade_body", as: "upgrade_body", type: "string" },
          { field: "upsell_prompt", as: "upsell_prompt", type: "string" },
          {
            field: "fit_above_threshold",
            as: "fit_above_threshold",
            type: "string",
          },
        ],
      ),

      // 4. Fit threshold condition
      n("4", "condition", "Strong upgrade fit? (score > 60)", 760, {
        conditionData: [
          {
            variable: "fit_above_threshold",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Personalized upgrade email
      emailNode(
        "5",
        "Upgrade recommendation email",
        1000,
        "{customer_email}",
        "{upgrade_subject}",
        "<p>Hi {customer_name},</p><p>{upgrade_body}</p><p>Based on your usage of <strong>{current_plan}</strong>, we recommend upgrading to <strong>{recommended_plan}</strong> before your renewal on {renewal_date}.</p><p>Potential savings: {monthly_savings}</p><p><a href='https://app.yourproduct.com/billing/upgrade?plan={recommended_plan}'>Review upgrade options</a></p>",
      ),

      // 6. In-app upsell prompt
      n("6", "hitApi", "Create in-app upsell prompt", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourproduct.com/v1/prompts",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_email", value: "{customer_email}" },
            { key: "prompt_type", value: "upgrade_upsell" },
            { key: "message", value: "{upsell_prompt}" },
            { key: "cta_text", value: "Upgrade to {recommended_plan}" },
            {
              key: "cta_url",
              value: "/billing/upgrade?plan={recommended_plan}",
            },
            { key: "expires_at", value: "{renewal_date}" },
          ],
          variables: [
            {
              name: "prompt_id",
              customName: "upsell_prompt_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. AM Slack alert
      slackNode(
        "7",
        "Slack: AM alert for upgrade opportunity",
        1480,
        "*UPGRADE OPPORTUNITY — Renewal approaching*\n\nCustomer: {customer_name} ({customer_email})\nCurrent plan: {current_plan} | Recommended: {recommended_plan}\nUpgrade score: {upgrade_score}/100\nUsage: {current_usage} | Renewal: {renewal_date}\nSavings: {monthly_savings}\n\nEmail + in-app prompt sent. Follow up for enterprise upsell.",
      ),

      // 8. CRM opportunity creation
      n("8", "hitApi", "Create upgrade opportunity in CRM", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"dealname":"Upgrade: {customer_name} to {recommended_plan}","dealstage":"qualifiedtobuy","pipeline":"default","amount":"{monthly_savings}","closedate":"{renewal_date}","description":"Upgrade score: {upgrade_score}. Current plan: {current_plan}. Usage: {current_usage}. Auto-detected at-limit customer near renewal."}',
            },
          ],
          variables: [{ name: "id", customName: "deal_id", type: "string" }],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // strong fit -> email
      e("4", "8", "output_2"), // weak fit -> just CRM logging
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Upgrade to Higher Plan Before Renewal** — AI-scored upgrade recommendations with multi-channel upsell for renewal-approaching customers.\n\n1. Weekly scan finds customers at 80%+ plan limits within 30 days of renewal\n2. **Stripe usage pull** retrieves current consumption vs. limits\n3. **AI scores upgrade fit** and recommends optimal plan with savings estimate\n4. Fit threshold routing: strong candidates get full treatment\n5. **Upgrade email** with personalized pitch and savings calculation\n6. **In-app upsell prompt** shown until renewal date\n7. **AM Slack alert** for enterprise upsell follow-up\n8. **CRM deal created** to track upgrade opportunity\n\n**To activate:** Say "connect stripe", "connect openai", "connect email", "connect slack", "connect hubspot"`,
  },

  // ===================================================================
  // 168. USAGE MILESTONE CELEBRATION (7 nodes)
  // ===================================================================

  "Usage Milestone Celebration": {
    nodes: [
      trigger("Milestone event: User hits usage landmark", "webhook_app", {
        source: "Segment / Internal Analytics",
        event: "user.milestone_reached",
        howItStarts:
          "A user reaches a significant usage milestone (e.g., 100th workflow run, 1000th contact, 50th campaign sent)",
        setup:
          "Configure Segment to fire when predefined milestone thresholds are crossed. Include milestone type, value, and user context.",
        inputFields: [
          {
            name: "user_email",
            type: "string",
            example: "achiever@company.com",
          },
          { name: "user_name", type: "string", example: "Skyler Green" },
          { name: "milestone_type", type: "string", example: "workflows_run" },
          { name: "milestone_value", type: "string", example: "100" },
          {
            name: "milestone_label",
            type: "string",
            example: "100 Workflow Runs",
          },
        ],
      }),

      // 2. Context enrichment
      n("2", "hitApi", "Pull user context + achievement history", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourproduct.com/v1/users/{user_email}/achievements",
          headers: [{ key: "Authorization", value: "Bearer {api_token}" }],
          variables: [
            {
              name: "total_achievements",
              customName: "total_achievements",
              type: "number",
            },
            {
              name: "member_since",
              customName: "member_since",
              type: "string",
            },
            {
              name: "account_tier",
              customName: "account_tier",
              type: "string",
            },
            {
              name: "previous_milestone",
              customName: "previous_milestone",
              type: "string",
            },
            {
              name: "community_member",
              customName: "is_community_member",
              type: "boolean",
            },
          ],
        },
      }),

      // 3. AI celebration content
      aiNode(
        "3",
        "AI: Generate celebration content + badge",
        520,
        "You are a gamification and celebration specialist. Create a heartfelt, specific celebration message for this usage milestone. Include a fun badge name, encourage sharing, and suggest the next milestone. Respond JSON: {celebration_subject: string, celebration_body: string, badge_name: string, badge_emoji: string, next_milestone: string, share_message: string, is_shareable: string}",
        "User: {user_name}, Milestone: {milestone_label} ({milestone_type}: {milestone_value}), Total achievements: {total_achievements}, Member since: {member_since}, Account tier: {account_tier}, Previous milestone: {previous_milestone}, Community member: {is_community_member}",
        [
          {
            field: "celebration_subject",
            as: "celebration_subject",
            type: "string",
          },
          { field: "celebration_body", as: "celebration_body", type: "string" },
          { field: "badge_name", as: "badge_name", type: "string" },
          { field: "badge_emoji", as: "badge_emoji", type: "string" },
          { field: "next_milestone", as: "next_milestone", type: "string" },
          { field: "share_message", as: "share_message", type: "string" },
          { field: "is_shareable", as: "is_shareable", type: "string" },
        ],
      ),

      // 4. Badge asset generation
      n("4", "hitApi", "Assign badge to user profile", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.yourproduct.com/v1/users/{user_email}/badges",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "badge_name", value: "{badge_name}" },
            { key: "badge_emoji", value: "{badge_emoji}" },
            { key: "milestone_type", value: "{milestone_type}" },
            { key: "milestone_value", value: "{milestone_value}" },
            { key: "earned_at", value: "{current_date}" },
          ],
          variables: [
            { name: "badge_id", customName: "badge_id", type: "string" },
            { name: "badge_url", customName: "badge_url", type: "string" },
          ],
        },
      }),

      // 5. Celebration email
      emailNode(
        "5",
        "Milestone celebration email",
        1000,
        "{user_email}",
        "{celebration_subject}",
        "<h2>{badge_emoji} {badge_name}</h2><p>Hi {user_name},</p><p>{celebration_body}</p><p><strong>Your badge:</strong> {badge_name} {badge_emoji}</p><p><strong>Next milestone:</strong> {next_milestone}</p><p>You've been a member since {member_since} and earned {total_achievements} achievements so far!</p><p><a href='https://app.yourproduct.com/achievements/{user_email}'>View your achievement gallery</a></p>",
      ),

      // 6. Community sharing condition
      n("6", "condition", "Shareable milestone + community member?", 1240, {
        conditionData: [
          {
            variable: "is_shareable",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Internal CS Slack notification
      slackNode(
        "7",
        "Slack: CS celebration notification",
        1480,
        "*MILESTONE CELEBRATION*\n\nUser: {user_name} ({user_email})\nMilestone: {milestone_label} ({badge_emoji} {badge_name})\nAccount tier: {account_tier} | Member since: {member_since}\nTotal achievements: {total_achievements}\nNext milestone: {next_milestone}\n\nShare message: {share_message}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // shareable -> CS notification for amplification
    ],
    summary: `**Usage Milestone Celebration** — AI-generated celebrations with badges and community sharing for usage landmarks.\n\n1. Milestone event fires when user crosses usage threshold\n2. **Achievement history** pull retrieves context and previous milestones\n3. **AI generates** celebration content, badge name, and sharing message\n4. **Badge assigned** to user profile with visual asset\n5. **Celebration email** with badge, achievement gallery link, and next milestone\n6. Shareability check: community members with notable milestones\n7. **CS Slack notification** for amplification and social proof opportunities\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 169. USAGE TIPS AFTER FIRST PURCHASE (7 nodes)
  // ===================================================================

  "Usage Tips After First Purchase": {
    nodes: [
      trigger("First purchase: Order confirmed", "webhook_app", {
        source: "Shopify / Stripe",
        event: "order.created",
        howItStarts:
          "A customer makes their first purchase, triggering a product usage tips sequence",
        setup:
          "Connect Shopify or Stripe webhook for new orders. Filter for first-time buyers (order_count = 1). Include product details and customer info.",
        inputFields: [
          {
            name: "customer_email",
            type: "string",
            example: "buyer@example.com",
          },
          { name: "customer_name", type: "string", example: "Robin Park" },
          { name: "customer_phone", type: "string", example: "+15552223333" },
          {
            name: "product_name",
            type: "string",
            example: "Smart Home Hub Pro",
          },
          { name: "product_category", type: "string", example: "smart_home" },
          { name: "order_id", type: "string", example: "ORD-12345" },
        ],
      }),

      // 2. Product knowledge pull
      n("2", "hitApi", "Pull product knowledge from catalog", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourproduct.com/v1/products/{product_category}/knowledge",
          headers: [{ key: "Authorization", value: "Bearer {api_token}" }],
          variables: [
            {
              name: "setup_guide_url",
              customName: "setup_guide_url",
              type: "string",
            },
            { name: "tips", customName: "product_tips", type: "string" },
            {
              name: "advanced_features",
              customName: "advanced_features",
              type: "string",
            },
            { name: "faq_url", customName: "faq_url", type: "string" },
            {
              name: "community_url",
              customName: "community_url",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI tip curation
      aiNode(
        "3",
        "AI: Curate personalized usage tips sequence",
        520,
        "You are a product educator. Based on the product and customer context, curate two sets of tips: Day-1 getting-started basics and Day-5 advanced tips. Make them specific to the product. Respond JSON: {day1_subject: string, day1_tips: string, day5_subject: string, day5_tips: string, sms_tip: string, engagement_hook: string, has_engaged: string}",
        "Customer: {customer_name}, Product: {product_name} ({product_category}), Order: {order_id}, Setup guide: {setup_guide_url}, Product tips: {product_tips}, Advanced features: {advanced_features}",
        [
          { field: "day1_subject", as: "day1_subject", type: "string" },
          { field: "day1_tips", as: "day1_tips", type: "string" },
          { field: "day5_subject", as: "day5_subject", type: "string" },
          { field: "day5_tips", as: "day5_tips", type: "string" },
          { field: "sms_tip", as: "sms_tip", type: "string" },
          { field: "engagement_hook", as: "engagement_hook", type: "string" },
          { field: "has_engaged", as: "has_engaged", type: "string" },
        ],
      ),

      // 4. Day-1 getting-started email
      emailNode(
        "4",
        "Day-1: Getting started with your purchase",
        760,
        "{customer_email}",
        "{day1_subject}",
        "<h2>Welcome to your new {product_name}!</h2><p>Hi {customer_name},</p><p>{day1_tips}</p><p><a href='{setup_guide_url}'>Full setup guide</a> | <a href='{faq_url}'>FAQs</a> | <a href='{community_url}'>Community</a></p><p>{engagement_hook}</p>",
      ),

      // 5. Day-5 delay + advanced email
      n("5", "delayAction", "Wait 5 days for advanced tips", 1000, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 5,
          delayUnit: "days",
        },
      }),

      // 5b. Day-5 advanced email
      emailNode(
        "5b",
        "Day-5: Advanced tips + hidden features",
        1240,
        "{customer_email}",
        "{day5_subject}",
        "<p>Hi {customer_name},</p><p>You've had your {product_name} for 5 days now! Ready for the next level?</p><p>{day5_tips}</p><p><strong>Advanced features to explore:</strong> {advanced_features}</p><p><a href='{community_url}'>Join the community</a> to learn from other users.</p>",
      ),

      // 6. Engagement condition
      n("6", "condition", "Engaged with tips content?", 1480, {
        conditionData: [
          {
            variable: "has_engaged",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. SMS tips via Twilio
      n("7", "sendSMS", "SMS: Quick tip + community invite", 1720, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message:
            "{sms_tip} Join our community for more tips: {community_url}",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "5b"),
      e("5b", "6"),
      e("6", "7", "output_2"), // not engaged -> SMS tips
    ],
    summary: `**Usage Tips After First Purchase** — AI-curated product education sequence with Day-1 and Day-5 tips.\n\n1. First purchase triggers via Shopify/Stripe webhook\n2. **Product knowledge pull** retrieves setup guides, tips, and community links\n3. **AI curates** Day-1 basics and Day-5 advanced tips specific to the product\n4. **Day-1 email** with getting-started tips and setup guide\n5. 5-day wait for product familiarity\n6. **Day-5 email** with advanced tips and hidden features\n7. Not engaging? **SMS tip** via Twilio with community invite\n\n**To activate:** Say "connect openai", "connect email", "connect twilio"`,
  },

  // ===================================================================
  // 170. VOICE TO ACTION AGENT (9 nodes)
  // ===================================================================

  "Voice to Action Agent": {
    nodes: [
      trigger("Voice input: Audio command received", "webhook_app", {
        source: "Mobile App / Voice Assistant",
        event: "voice.command_received",
        howItStarts:
          "A user submits a voice command that needs to be transcribed, parsed, and executed as an action",
        setup:
          "Connect your mobile app or voice assistant webhook. Send the audio file URL and user context.",
        inputFields: [
          { name: "user_email", type: "string", example: "voice@user.com" },
          { name: "user_name", type: "string", example: "Finley Brooks" },
          {
            name: "audio_url",
            type: "string",
            example: "https://storage.example.com/audio/cmd_123.webm",
          },
          { name: "device_type", type: "string", example: "mobile_ios" },
        ],
      }),

      // 2. Whisper transcription
      n("2", "hitApi", "Transcribe voice via OpenAI Whisper", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/audio/transcriptions",
          headers: [{ key: "Authorization", value: "Bearer {openai_key}" }],
          body: [
            { key: "model", value: "whisper-1" },
            { key: "file", value: "{audio_url}" },
            { key: "language", value: "en" },
          ],
          variables: [
            { name: "text", customName: "transcription", type: "string" },
          ],
        },
      }),

      // 3. AI intent + action extraction
      aiNode(
        "3",
        "AI: Extract intent + action from transcript",
        520,
        "You are a voice command parser. Extract the user intent and specific action from this transcription. Identify entities (contacts, dates, amounts) and determine the target system (CRM, calendar, task manager, email). Respond JSON: {intent: string, action_type: string, target_system: string, entities: string, contact_name: string, date_value: string, amount_value: string, action_description: string, confirmation_summary: string, requires_routing: string}",
        "Transcription: {transcription}, User: {user_name} ({user_email}), Device: {device_type}",
        [
          { field: "intent", as: "parsed_intent", type: "string" },
          { field: "action_type", as: "action_type", type: "string" },
          { field: "target_system", as: "target_system", type: "string" },
          { field: "entities", as: "extracted_entities", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "date_value", as: "date_value", type: "string" },
          { field: "amount_value", as: "amount_value", type: "string" },
          {
            field: "action_description",
            as: "action_description",
            type: "string",
          },
          {
            field: "confirmation_summary",
            as: "confirmation_summary",
            type: "string",
          },
          { field: "requires_routing", as: "requires_routing", type: "string" },
        ],
      ),

      // 4. Entity resolution (runCode)
      n("4", "runCode", "Resolve entities + validate action", 760, {
        runCodeData: {
          code: "const action = input.action_type; const target = input.target_system; const isValid = action && target && action !== 'unknown'; output.is_valid_action = isValid; output.action_payload = JSON.stringify({type: action, target: target, contact: input.contact_name, date: input.date_value, amount: input.amount_value, description: input.action_description}); output.routing_key = target + '_' + action;",
        },
      }),

      // 5. Action routing condition
      n("5", "condition", "Valid action with known target?", 1000, {
        conditionData: [
          {
            variable: "is_valid_action",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Task/CRM/calendar execution
      n("6", "hitApi", "Execute action: Create task/CRM entry", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_task_subject":"{action_description}","hs_task_body":"Voice command: {transcription}\\nIntent: {parsed_intent}\\nEntities: {extracted_entities}\\nContact: {contact_name}\\nDate: {date_value}","hs_task_status":"NOT_STARTED","hs_task_priority":"MEDIUM","hs_task_type":"{action_type}"}',
            },
          ],
          variables: [
            { name: "id", customName: "created_task_id", type: "string" },
          ],
        },
      }),

      // 7. Confirmation summary email
      emailNode(
        "7",
        "Confirmation email: Voice action executed",
        1480,
        "{user_email}",
        "Voice command executed: {action_description}",
        '<p>Hi {user_name},</p><p>Your voice command has been processed:</p><p><strong>You said:</strong> "{transcription}"</p><p><strong>Action taken:</strong> {confirmation_summary}</p><p><strong>System:</strong> {target_system}</p><p><strong>Details:</strong> {action_description}</p><p>Task ID: {created_task_id}</p>',
      ),

      // 8. Failure bundle (Slack) — invalid action path
      slackNode(
        "8",
        "Slack: Voice command failed to parse",
        1240,
        "*VOICE COMMAND PARSE FAILURE*\n\nUser: {user_name} ({user_email})\nTranscription: {transcription}\nIntent: {parsed_intent}\nAction type: {action_type}\nTarget: {target_system}\n\nCould not resolve to a valid action. Routing key: {routing_key}",
        460,
      ),

      // 9. Audit trail logging
      n("9", "hitApi", "Log voice action to audit trail", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{user_email}" },
            { key: "event", value: "voice_action_processed" },
            {
              key: "properties",
              value:
                '{"transcription":"{transcription}","intent":"{parsed_intent}","action_type":"{action_type}","target_system":"{target_system}","entities":"{extracted_entities}","is_valid":"{is_valid_action}","device":"{device_type}","task_id":"{created_task_id}"}',
            },
          ],
          variables: [
            { name: "success", customName: "audit_logged", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // valid -> execute action
      e("5", "8", "output_2"), // invalid -> failure alert
      e("6", "7"),
      e("7", "9"),
      e("8", "9"),
    ],
    summary: `**Voice to Action Agent** — End-to-end voice command pipeline from transcription to CRM action execution.\n\n1. Voice command arrives via mobile app or assistant\n2. **Whisper transcription** converts audio to text\n3. **AI extracts intent**, action type, target system, and entities\n4. **RunCode resolves entities** and validates action feasibility\n5. Routing: valid actions execute, invalid ones alert support\n6. **CRM task created** based on parsed voice command\n7. **Confirmation email** with transcription and action summary\n8. Invalid: **Slack failure alert** for unresolvable commands\n9. **Audit trail** logs all voice commands for analytics\n\n**To activate:** Say "connect openai", "connect hubspot", "connect email", "connect slack", "connect segment"`,
  },

  // ===================================================================
  // 171. WELCOME PLUS SURVEY PLUS PERSONALIZATION (8 nodes)
  // ===================================================================

  "Welcome plus Survey plus Personalization": {
    nodes: [
      trigger("New user: Account created", "webhook_app", {
        source: "Auth0 / Firebase Auth",
        event: "user.created",
        howItStarts:
          "A new user account is created, triggering the welcome + survey + personalization pipeline",
        setup:
          "Connect Auth0 or Firebase Auth webhook for new user creation. Include email, name, and signup source.",
        inputFields: [
          {
            name: "user_email",
            type: "string",
            example: "newuser@company.com",
          },
          { name: "user_name", type: "string", example: "Emery Nguyen" },
          { name: "signup_source", type: "string", example: "product_hunt" },
        ],
      }),

      // 2. Welcome + survey email
      emailNode(
        "2",
        "Welcome email with embedded survey",
        280,
        "{user_email}",
        "Welcome, {user_name}! Help us personalize your experience (30 sec)",
        "<h2>Welcome to the platform!</h2><p>Hi {user_name},</p><p>We're excited to have you! To give you the best possible experience, please answer 3 quick questions:</p><p><a href='https://app.yourproduct.com/onboarding-survey?user={user_email}&q=role'>What is your role?</a></p><p><a href='https://app.yourproduct.com/onboarding-survey?user={user_email}&q=goals'>What are your main goals?</a></p><p><a href='https://app.yourproduct.com/onboarding-survey?user={user_email}&q=experience'>How experienced are you?</a></p><p>This takes about 30 seconds and makes a huge difference!</p>",
      ),

      // 3. Survey delay + reminder condition
      n("3", "delayAction", "Wait 24 hours for survey response", 520, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 24,
          delayUnit: "hours",
        },
      }),

      // 3b. Survey response check
      n("3b", "condition", "Survey response received?", 760, {
        conditionData: [
          {
            variable: "signup_source",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Fallback profiling (AI) — no survey response
      aiNode(
        "4",
        "AI: Fallback profiling from signup signals",
        1000,
        "You are a user profiling specialist. This user did not complete the survey. Based on their signup source and email domain, infer their likely role, goals, and experience level. Respond JSON: {inferred_role: string, inferred_goals: string, inferred_experience: string, persona_label: string, confidence: string}",
        "User: {user_name} ({user_email}), Signup source: {signup_source}",
        [
          { field: "inferred_role", as: "user_role", type: "string" },
          { field: "inferred_goals", as: "user_goals", type: "string" },
          {
            field: "inferred_experience",
            as: "user_experience",
            type: "string",
          },
          { field: "persona_label", as: "persona_label", type: "string" },
          { field: "confidence", as: "profile_confidence", type: "string" },
        ],
        460,
      ),

      // 5. Preference analysis
      aiNode(
        "5",
        "AI: Analyze preferences + recommend setup",
        1240,
        "You are an onboarding personalization engine. Based on the user profile (from survey or inference), recommend app configuration, default dashboard, and first-steps sequence. Respond JSON: {recommended_dashboard: string, first_steps: string, feature_highlights: string, personalized_subject: string, personalized_body: string}",
        "User: {user_name}, Role: {user_role}, Goals: {user_goals}, Experience: {user_experience}, Persona: {persona_label}, Signup source: {signup_source}",
        [
          {
            field: "recommended_dashboard",
            as: "recommended_dashboard",
            type: "string",
          },
          { field: "first_steps", as: "first_steps", type: "string" },
          {
            field: "feature_highlights",
            as: "feature_highlights",
            type: "string",
          },
          {
            field: "personalized_subject",
            as: "personalized_subject",
            type: "string",
          },
          {
            field: "personalized_body",
            as: "personalized_body",
            type: "string",
          },
        ],
      ),

      // 6. App personalization (runCode)
      n(
        "6",
        "runCode",
        "Apply personalization settings to user profile",
        1480,
        {
          runCodeData: {
            code: "output.personalization_config = JSON.stringify({dashboard: input.recommended_dashboard, first_steps: input.first_steps.split(',').map(s => s.trim()), persona: input.persona_label, role: input.user_role, experience: input.user_experience}); output.applied = true; output.config_summary = 'Dashboard: ' + input.recommended_dashboard + ', Persona: ' + input.persona_label;",
          },
        },
      ),

      // 7. Personalized onboarding email
      emailNode(
        "7",
        "Personalized onboarding email",
        1720,
        "{user_email}",
        "{personalized_subject}",
        "<p>Hi {user_name},</p><p>{personalized_body}</p><p><strong>Your personalized setup:</strong></p><ul><li>Dashboard: {recommended_dashboard}</li><li>First steps: {first_steps}</li><li>Key features for you: {feature_highlights}</li></ul><p><a href='https://app.yourproduct.com/dashboard?persona={persona_label}'>Launch your personalized experience</a></p>",
      ),

      // 8. CS Slack notification
      slackNode(
        "8",
        "Slack: CS notification for new personalized user",
        1960,
        "*NEW USER ONBOARDED + PERSONALIZED*\n\nUser: {user_name} ({user_email})\nSignup source: {signup_source}\nPersona: {persona_label} | Role: {user_role}\nGoals: {user_goals} | Experience: {user_experience}\nDashboard: {recommended_dashboard}\nFirst steps: {first_steps}\nConfig: {config_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "3b"),
      e("3b", "5", "output_1"), // survey done -> preference analysis
      e("3b", "4", "output_2"), // no survey -> AI fallback profiling
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Welcome plus Survey plus Personalization** — Full onboarding pipeline with survey, AI fallback profiling, and app personalization.\n\n1. New user triggers welcome pipeline\n2. **Welcome email** with embedded 3-question survey\n3. 24-hour wait for survey response\n4. No response? **AI fallback profiling** from signup signals and email domain\n5. **AI preference analysis** recommends dashboard, features, and first steps\n6. **RunCode applies** personalization settings to user profile\n7. **Personalized onboarding email** with tailored setup guide\n8. **CS Slack notification** for awareness and potential outreach\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 172. WORKFLOW PERFORMANCE OPTIMIZER (8 nodes)
  // ===================================================================

  "Workflow Performance Optimizer": {
    nodes: [
      trigger("Weekly scan: Workflow performance review", "schedule", {
        schedule: "Every Monday at 6:00 AM",
        howItStarts:
          "Weekly automated review of all active workflows to detect performance deviations and recommend optimizations",
        setup:
          "Activate this workflow. It runs weekly to analyze all active workflow metrics and surface anomalies.",
        inputFields: [
          { name: "admin_email", type: "string", example: "ops@company.com" },
          { name: "admin_name", type: "string", example: "Ops Team" },
          { name: "review_period", type: "string", example: "7_days" },
        ],
      }),

      // 2. Historical baseline pull
      n("2", "hitApi", "Pull workflow performance baselines", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourproduct.com/v1/analytics/workflows?period={review_period}&metrics=runs,success_rate,avg_duration,error_rate,email_open_rate,sms_delivery_rate",
          headers: [{ key: "Authorization", value: "Bearer {api_token}" }],
          variables: [
            {
              name: "total_workflows",
              customName: "total_workflows",
              type: "number",
            },
            { name: "total_runs", customName: "total_runs", type: "number" },
            {
              name: "avg_success_rate",
              customName: "avg_success_rate",
              type: "number",
            },
            {
              name: "avg_duration_ms",
              customName: "avg_duration_ms",
              type: "number",
            },
            {
              name: "avg_error_rate",
              customName: "avg_error_rate",
              type: "number",
            },
            { name: "top_errors", customName: "top_errors", type: "string" },
            {
              name: "slowest_workflows",
              customName: "slowest_workflows",
              type: "string",
            },
          ],
        },
      }),

      // 3. Deviation detection (runCode)
      n("3", "runCode", "Detect performance deviations from baseline", 520, {
        runCodeData: {
          code: "const successRate = parseFloat(input.avg_success_rate) || 0; const errorRate = parseFloat(input.avg_error_rate) || 0; const duration = parseFloat(input.avg_duration_ms) || 0; output.has_deviation = (successRate < 95 || errorRate > 5 || duration > 30000) ? 'yes' : 'no'; output.severity = successRate < 80 ? 'critical' : successRate < 90 ? 'warning' : errorRate > 10 ? 'critical' : errorRate > 5 ? 'warning' : 'normal'; output.deviation_summary = 'Success: ' + successRate + '%, Errors: ' + errorRate + '%, Avg duration: ' + Math.round(duration/1000) + 's'; output.needs_immediate_action = (successRate < 80 || errorRate > 10) ? 'yes' : 'no';",
        },
      }),

      // 4. AI analysis + recommendations
      aiNode(
        "4",
        "AI: Analyze deviations + recommend fixes",
        760,
        "You are a workflow operations analyst. Analyze the performance metrics and deviations, identify root causes, and recommend specific optimizations. Prioritize by business impact. Respond JSON: {analysis_summary: string, root_causes: string, recommendations: string, priority_fixes: string, estimated_improvement: string, report_subject: string, report_body: string, is_severe: string}",
        "Total workflows: {total_workflows}, Total runs: {total_runs}, Success rate: {avg_success_rate}%, Error rate: {avg_error_rate}%, Avg duration: {avg_duration_ms}ms, Top errors: {top_errors}, Slowest: {slowest_workflows}, Deviation: {deviation_summary}, Severity: {severity}",
        [
          { field: "analysis_summary", as: "analysis_summary", type: "string" },
          { field: "root_causes", as: "root_causes", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "priority_fixes", as: "priority_fixes", type: "string" },
          {
            field: "estimated_improvement",
            as: "estimated_improvement",
            type: "string",
          },
          { field: "report_subject", as: "report_subject", type: "string" },
          { field: "report_body", as: "report_body", type: "string" },
          { field: "is_severe", as: "is_severe", type: "string" },
        ],
      ),

      // 5. Severity condition
      n("5", "condition", "Severe deviation? Needs immediate action?", 1000, {
        conditionData: [
          {
            variable: "needs_immediate_action",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Immediate Slack alert
      slackNode(
        "6",
        "Slack: Immediate ops alert for severe deviation",
        1240,
        "*SEVERE WORKFLOW PERFORMANCE DEVIATION*\n\nSeverity: {severity}\n{deviation_summary}\n\nRoot causes: {root_causes}\nPriority fixes: {priority_fixes}\n\nTotal workflows: {total_workflows} | Runs: {total_runs}\nTop errors: {top_errors}\nSlowest: {slowest_workflows}\n\nImmediate action required.",
        460,
      ),

      // 7. Weekly report email
      emailNode(
        "7",
        "Weekly workflow performance report",
        1480,
        "{admin_email}",
        "{report_subject}",
        "<h2>Weekly Workflow Performance Report</h2><p>{report_body}</p><h3>Key Metrics</h3><ul><li>Total workflows: {total_workflows}</li><li>Total runs: {total_runs}</li><li>Success rate: {avg_success_rate}%</li><li>Error rate: {avg_error_rate}%</li><li>Avg duration: {avg_duration_ms}ms</li></ul><h3>Analysis</h3><p>{analysis_summary}</p><h3>Root Causes</h3><p>{root_causes}</p><h3>Recommendations</h3><p>{recommendations}</p><h3>Priority Fixes</h3><p>{priority_fixes}</p><p>Estimated improvement: {estimated_improvement}</p>",
      ),

      // 8. Ops task creation
      n("8", "hitApi", "Create ops tasks for priority fixes", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"workspace":"{asana_workspace_id}","projects":["{ops_project_id}"],"name":"Workflow Perf: {severity} - {deviation_summary}","notes":"Analysis: {analysis_summary}\\n\\nRoot causes: {root_causes}\\n\\nPriority fixes: {priority_fixes}\\n\\nRecommendations: {recommendations}\\n\\nEstimated improvement: {estimated_improvement}","due_on":"{task_due_date}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "ops_task_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // severe -> immediate Slack alert
      e("5", "7", "output_2"), // not severe -> weekly report only
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Workflow Performance Optimizer** — Automated weekly performance review with AI-powered root cause analysis and optimization recommendations.\n\n1. Weekly schedule triggers comprehensive workflow performance scan\n2. **Analytics pull** retrieves success rates, error rates, durations, and top errors\n3. **RunCode detects** deviations from performance baselines\n4. **AI analyzes** root causes and recommends prioritized fixes\n5. Severity routing: critical issues get immediate attention\n6. Severe: **Slack alert** for immediate ops intervention\n7. **Weekly report email** with full analysis and recommendations\n8. **Asana task** created for tracking priority fixes\n\n**To activate:** Say "connect openai", "connect email", "connect slack", "connect asana"`,
  },
};
